import React, { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import {
  X,
  User,
  Heart,
  ShieldCheck,
  Phone,
  Mail,
  Lock,
  ArrowRight,
  Upload,
  CheckCircle2,
  FileText,
  CreditCard,
  Camera,
  Check,
  AlertCircle,
  Sparkles,
  ArrowLeft,
  Eye,
  Smartphone,
  Copy,
  Timer,
  Wallet,
  Calendar,
  Clock,
  Briefcase,
  Compass,
  Crown,
  Search,
  MessageSquare,
  UserX,
  UserPlus,
  RotateCcw,
  HelpCircle,
  KeyRound
} from 'lucide-react';
import { PairUpLogo } from './common/PairUpLogo';
import { LanguageSwitcher } from './common/LanguageSwitcher';
import { DocumentUploadModal } from './DocumentUploadModal';
import { FaceVerificationModal } from './FaceVerificationModal';
import { DynamicLocationSearchBox } from './location/DynamicLocationSearchBox';
import {
  auth,
  syncUserProfileToFirestore,
  checkEmailExistsInFirestore,
  checkPhoneExistsInFirestore,
} from '../lib/firebase';
import {
  CompanionStatus,
  CompanionMembershipPlan,
  MembershipSubscription,
  UserProfile,
  CompanionProfileStatus,
  MembershipStatus,
  PaymentStatus,
} from '../types';
import { createPaymentOrder, verifyPaymentWithBackend } from '../lib/paymentService';
import { apiFetch as fetch } from '../lib/apiConfig';
import { DateOfBirthInput } from './common/DateOfBirthInput';
import { validateAdultDob } from '../utils/dobUtils';
import { RazorpayCheckout, type RazorpayCheckoutResponse } from './RazorpayCheckout';

// Audio chime for simulated OTP delivery
const playOtpSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(784, ctx.currentTime); // G5
    osc.frequency.setValueAtTime(1046.5, ctx.currentTime + 0.1); // C6
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) {
    // ignore
  }
};

// ----------------------------------------------------
// REGISTRY & VALIDATION HELPERS (User Auth Spec & Login Rule)
// ----------------------------------------------------
export interface RegisteredUser {
  email: string;
  phone: string;
  name?: string;
  displayName?: string;
  legalName?: string;
  accountType?: 'client' | 'companion';
  password?: string;
  companionStatus?: string;
  isIdVerified?: boolean;
  isCompleted?: boolean;
}

export const normalizePhone = (phone: string): string => {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }
  return digits;
};

const getStoredRegisteredUsers = (): RegisteredUser[] => {
  try {
    const raw = localStorage.getItem('pairup_registered_users');
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry: any) =>
      entry &&
      typeof entry === 'object' &&
      typeof entry.phone === 'string' &&
      typeof entry.accountType === 'string' &&
      typeof entry.email === 'string'
    );
  } catch {
    return [];
  }
};

const checkEmailRegistered = async (email: string): Promise<boolean> => {
  const normalized = email.trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) return false;
  // 1. Local permanent registry of COMPLETED accounts ONLY
  const list = getStoredRegisteredUsers();
  if (list.some((u) => u.email && u.email.toLowerCase() === normalized && u.isCompleted === true)) {
    return true;
  }
  // 2. Firestore cloud database of COMPLETED accounts ONLY
  try {
    const inDb = await checkEmailExistsInFirestore(normalized);
    if (inDb) return true;
  } catch (e) {
    console.warn('Firestore email check error:', e);
  }
  return false;
};

const checkPhoneRegistered = async (phone: string): Promise<boolean> => {
  const normalized = normalizePhone(phone);
  if (!normalized || normalized.length < 10) return false;
  // 1. Local permanent registry of COMPLETED accounts ONLY
  const list = getStoredRegisteredUsers();
  if (list.some((u) => normalizePhone(u.phone) === normalized && u.isCompleted === true)) {
    return true;
  }
  // 2. Firestore cloud database of COMPLETED accounts ONLY
  try {
    const inDb = await checkPhoneExistsInFirestore(normalized);
    if (inDb) return true;
  } catch (e) {
    console.warn('Firestore phone check error:', e);
  }
  return false;
};

// Check if an account exists for login (Email or Phone)
export interface AccountCheckResult {
  exists: boolean;
  user?: RegisteredUser;
  reason: 'ACCOUNT_FOUND' | 'ACCOUNT_NOT_FOUND';
}

export const checkAccountExists = async (identifier: string): Promise<AccountCheckResult> => {
  const trimmed = identifier.trim();
  if (!trimmed) return { exists: false, reason: 'ACCOUNT_NOT_FOUND' };

  const isEmail = trimmed.includes('@');
  const normalizedEmail = trimmed.toLowerCase();
  const normalizedPhone = normalizePhone(trimmed);

  // 1. Check local registry of COMPLETED accounts ONLY
  const list = getStoredRegisteredUsers();
  for (const reg of list) {
    if (reg.isCompleted !== true) continue;
    const regEmail = (reg.email || '').toLowerCase().trim();
    const regPhone = normalizePhone(reg.phone || '');

    if (isEmail && regEmail === normalizedEmail) {
      return { exists: true, user: reg, reason: 'ACCOUNT_FOUND' };
    }
    if (!isEmail && normalizedPhone.length >= 10 && regPhone === normalizedPhone) {
      return { exists: true, user: reg, reason: 'ACCOUNT_FOUND' };
    }
  }

  // 2. Check active user profile in localStorage
  try {
    const activeRaw = localStorage.getItem('pairup_user_profile_data');
    if (activeRaw) {
      const parsed = JSON.parse(activeRaw);
      const profEmail = (parsed.email || '').toLowerCase().trim();
      const profPhone = normalizePhone(parsed.phone || '');

      const regMatch = list.find(
        (u) =>
          (profEmail && u.email.toLowerCase() === profEmail) ||
          (profPhone && normalizePhone(u.phone) === profPhone)
      );

      if (isEmail && profEmail === normalizedEmail) {
        return {
          exists: true,
          user: {
            email: profEmail,
            phone: profPhone,
            name: parsed.name,
            accountType: parsed.accountType || regMatch?.accountType || 'client',
            password: regMatch?.password,
            companionStatus: parsed.companionStatus,
          },
          reason: 'ACCOUNT_FOUND',
        };
      }
      if (!isEmail && normalizedPhone.length >= 10 && profPhone === normalizedPhone) {
        return {
          exists: true,
          user: {
            email: profEmail,
            phone: profPhone,
            name: parsed.name,
            accountType: parsed.accountType || regMatch?.accountType || 'client',
            password: regMatch?.password,
            companionStatus: parsed.companionStatus,
          },
          reason: 'ACCOUNT_FOUND',
        };
      }
    }
  } catch {}

  // 3. Check Firestore users collection
  try {
    if (isEmail) {
      const inDb = await checkEmailExistsInFirestore(normalizedEmail);
      if (inDb) {
        const regMatch = list.find((u) => u.email.toLowerCase() === normalizedEmail);
        return {
          exists: true,
          user: {
            email: normalizedEmail,
            phone: regMatch?.phone || '',
            name: regMatch?.name || normalizedEmail.split('@')[0],
            accountType: regMatch?.accountType || 'client',
            password: regMatch?.password,
          },
          reason: 'ACCOUNT_FOUND',
        };
      }
    } else if (normalizedPhone.length >= 10) {
      const inDb = await checkPhoneExistsInFirestore(normalizedPhone);
      if (inDb) {
        const regMatch = list.find((u) => normalizePhone(u.phone) === normalizedPhone);
        return {
          exists: true,
          user: {
            email: regMatch?.email || '',
            phone: normalizedPhone,
            name: regMatch?.name || `Member_${normalizedPhone.slice(-4)}`,
            accountType: regMatch?.accountType || 'client',
            password: regMatch?.password,
          },
          reason: 'ACCOUNT_FOUND',
        };
      }
    }
  } catch (err) {
    console.warn('Firestore account lookup:', err);
  }

  return { exists: false, reason: 'ACCOUNT_NOT_FOUND' };
};

// Password Requirements:
// Must be at least 8 characters, with letters and at least 1 number or special symbol
const isPasswordStrong = (pwd: string): boolean => {
  if (!pwd || pwd.length < 8) return false;
  const hasLetter = /[a-zA-Z]/.test(pwd);
  const hasNumberOrSymbol = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd);
  return hasLetter && hasNumberOrSymbol;
};

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode: 'login' | 'signup';
  initialAccountType?: 'client' | 'companion';
  onLoginSuccess: (
    userName: string,
    accountType?: 'client' | 'companion',
    companionProfile?: any,
    membership?: any
  ) => void;
  onOpenCompanionDashboard?: () => void;
  onOpenClientDashboard?: () => void;
  onFindCompanion?: () => void;
  onOpenBookings?: () => void;
  onOpenProfile?: () => void;
  onOpenWallet?: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode,
  initialAccountType = 'client',
  onLoginSuccess,
  onOpenCompanionDashboard,
  onOpenClientDashboard,
  onFindCompanion,
  onOpenBookings,
  onOpenProfile,
  onOpenWallet,
}) => {
  // Main Navigation Modes:
  // 'signup-choice' | 'client-signup' | 'companion-signup' | 'login'
  const [currentMode, setCurrentMode] = useState<
    'signup-choice' | 'client-signup' | 'companion-signup' | 'login'
  >(
    initialMode === 'login'
      ? 'login'
      : initialAccountType === 'companion'
      ? 'companion-signup'
      : 'signup-choice'
  );

  // Sync mode with props
  useEffect(() => {
    if (initialMode === 'login') {
      setCurrentMode('login');
    } else if (initialAccountType === 'companion') {
      setCurrentMode('companion-signup');
    } else {
      setCurrentMode('signup-choice');
    }
  }, [initialMode, initialAccountType]);

  // ----------------------------------------------------
  // CLIENT SIGNUP STATE (Spec Section 2)
  // Step 1 = Create Client Account (Legal Name, Display Name, Govt ID)
  // Step 2 = Mobile Verification (6-digit OTP)
  // Step 3 = Client Profile
  // Step 4 = Client Dashboard
  // ----------------------------------------------------
  const [clientStep, setClientStep] = useState<1 | 2 | 3 | 4>(1);
  const [clientLegalName, setClientLegalName] = useState(''); // Full Legal Name — Government ID অনুযায়ী (Private)
  const [clientDisplayName, setClientDisplayName] = useState(''); // Profile Name / Display Name — নিজের পছন্দমতো (Public)
  const [clientFullName, setClientFullName] = useState(''); // fallback/sync
  const [clientMobile, setClientMobile] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientConfirmPassword, setClientConfirmPassword] = useState('');
  const [clientIdType, setClientIdType] = useState('Aadhaar Card');
  const [clientIdNumber, setClientIdNumber] = useState('');
  const [clientIdDocUploaded, setClientIdDocUploaded] = useState(false);
  const [clientIdDocUrl, setClientIdDocUrl] = useState<string | null>(null);
  const [isClientDocModalOpen, setIsClientDocModalOpen] = useState(false);
  const [clientSelfieUploaded, setClientSelfieUploaded] = useState(false);
  const [clientIsIdVerified, setClientIsIdVerified] = useState(false);
  const [clientAgreedTerms, setClientAgreedTerms] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [clientErrorField, setClientErrorField] = useState<
    'email' | 'phone' | 'password' | 'confirmPassword' | 'legalName' | 'displayName' | 'idNumber' | null
  >(null);
  const [clientIsValidating, setClientIsValidating] = useState(false);

  // Client OTP State
  const [clientOtp, setClientOtp] = useState(['', '', '', '', '', '']);
  const [clientOtpSentCode, setClientOtpSentCode] = useState('');
  const [clientOtpTimer, setClientOtpTimer] = useState(30);
  const [clientShowNotification, setClientShowNotification] = useState(false);

  // Client Profile State (Step 3)
  const [clientPhoto, setClientPhoto] = useState(
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80'
  );
  const [clientDob, setClientDob] = useState('1998-05-14');
  const [clientGender, setClientGender] = useState('Male');
  const [clientCity, setClientCity] = useState('Kolkata');
  const [clientLanguage, setClientLanguage] = useState('English, Bengali, Hindi');
  const [clientAboutYou, setClientAboutYou] = useState(
    'Software engineer looking for friendly companionship for dinner dates, cultural events, and weekend café visits.'
  );
  const [clientLookingFor, setClientLookingFor] = useState('Dinner dates & social events');
  const [clientCompanionType, setClientCompanionType] = useState('Friendly & Conversational');
  const [clientDuration, setClientDuration] = useState('2-3 hours');

  // ----------------------------------------------------
  // COMPANION SIGNUP STATE (Spec Section 3-9)
  // Step 1 = Create Companion Account (Legal Name & Display Name)
  // Step 2 = Mobile Verification
  // Step 3 = Identity Verification (Govt ID + Selfie)
  // Step 4 = Profile Setup (Rate, Services, Bio, Availability)
  // Step 5 = Membership Selection (Starter, Pro, Premium)
  // Step 6 = Membership Checkout
  // Step 7 = Payment Success
  // Step 8 = Companion Dashboard
  // ----------------------------------------------------
  const [companionStep, setCompanionStep] = useState<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8>(1);
  const [compLegalName, setCompLegalName] = useState(''); // Full Legal Name — ID অনুযায়ী (Private)
  const [compFullName, setCompFullName] = useState('');
  const [compMobile, setCompMobile] = useState('');
  const [compEmail, setCompEmail] = useState('');
  const [compPassword, setCompPassword] = useState('');
  const [compConfirmPassword, setCompConfirmPassword] = useState('');
  const [compAgreedTerms, setCompAgreedTerms] = useState(false);
  const [compError, setCompError] = useState<string | null>(null);
  const [compErrorField, setCompErrorField] = useState<
    'email' | 'phone' | 'password' | 'confirmPassword' | 'legalName' | 'displayName' | null
  >(null);
  const [compIsValidating, setCompIsValidating] = useState(false);

  // Companion OTP
  const [compOtp, setCompOtp] = useState(['', '', '', '', '', '']);
  const [compOtpSentCode, setCompOtpSentCode] = useState('');
  const [compOtpTimer, setCompOtpTimer] = useState(30);
  const [compShowNotification, setCompShowNotification] = useState(false);

  // Companion Govt ID (Step 3)
  const [compIdType, setCompIdType] = useState('Aadhaar');
  const [compIdNumber, setCompIdNumber] = useState('');
  const [compIdDocUploaded, setCompIdDocUploaded] = useState(false);
  const [compIdDocUrl, setCompIdDocUrl] = useState<string | null>(null);
  const [compIdDocName, setCompIdDocName] = useState<string | null>(null);
  const [isCompDocModalOpen, setIsCompDocModalOpen] = useState(false);

  const [compSelfieUploaded, setCompSelfieUploaded] = useState(false);
  const [compSelfieUrl, setCompSelfieUrl] = useState<string | null>(null);
  const [isCompSelfieModalOpen, setIsCompSelfieModalOpen] = useState(false);
  const [compAgreedIdentityAccuracy, setCompAgreedIdentityAccuracy] = useState(false);
  const [compVerificationInProgress, setCompVerificationInProgress] = useState(false);

  // Companion Profile Setup (Step 4)
  const [compPhoto, setCompPhoto] = useState(
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80'
  );
  const [compDisplayName, setCompDisplayName] = useState(''); // Profile Name / Display Name — নিজের পছন্দমতো (Public)
  const [compDob, setCompDob] = useState('2000-01-01');
  const [compAge, setCompAge] = useState(26);
  const [compGender, setCompGender] = useState<'female' | 'male'>('female');
  const [compCity, setCompCity] = useState('Kolkata');
  const [compLanguages, setCompLanguages] = useState('English, Hindi, Bengali');
  const [compAvailability, setCompAvailability] = useState('Flexible Hours (Evenings & Weekends)');
  const [compAboutMe, setCompAboutMe] = useState(
    'Sociable, empathetic companion with great dining etiquette and passion for music, art, and intellectual conversation.'
  );
  const [compInterests, setCompInterests] = useState('Cafes, Live Music, Art Galleries, Fine Dining, Reading');
  const [compSkills, setCompSkills] = useState('Active Listening, Event Styling, Great Conversationalist');
  const [compExperience, setCompExperience] = useState('2+ years hosting social dinners & private networking events');
  const [compServices, setCompServices] = useState<string[]>([
    'Social companionship',
    'Conversation',
    'Events',
  ]);
  const [compHourlyRate, setCompHourlyRate] = useState(1200);
  const [compMinDuration, setCompMinDuration] = useState(1);

  // Companion Membership (Step 5 & 6)
  const [selectedPlan, setSelectedPlan] = useState<{
    id: CompanionMembershipPlan;
    name: string;
    price: number;
    period: string;
  }>({
    id: 'PROFESSIONAL',
    name: 'PROFESSIONAL',
    price: 1099,
    period: '/ month',
  });

  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [paymentFailedState, setPaymentFailedState] = useState(false);
  const [checkoutCancelledState, setCheckoutCancelledState] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentOrderKeyId, setCurrentOrderKeyId] = useState('');
  const [currentOrderAmountPaise, setCurrentOrderAmountPaise] = useState(0);
  const [paymentVerificationStatus, setPaymentVerificationStatus] = useState<'IDLE' | 'VERIFYING' | 'SUCCESS' | 'FAILED'>('IDLE');
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null);

  // Next billing date (30 days from now)
  const getNextBillingDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };
  const nextBillingDate = getNextBillingDate();

  // ----------------------------------------------------
  // LOGIN STATE (Spec Section 16 & Login Rule)
  // ----------------------------------------------------
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginIsOtpMode, setLoginIsOtpMode] = useState(false);
  const [loginOtpCode, setLoginOtpCode] = useState(['', '', '', '', '', '']);
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [loginOtpSentCode, setLoginOtpSentCode] = useState('');
  const [loginOtpTimer, setLoginOtpTimer] = useState(30);
  const [loginOtpNotification, setLoginOtpNotification] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginAccountNotFound, setLoginAccountNotFound] = useState(false);
  const [loginPasswordIncorrect, setLoginPasswordIncorrect] = useState(false);
  const [loginIsValidating, setLoginIsValidating] = useState(false);
  const [loginOtpTargetUser, setLoginOtpTargetUser] = useState<RegisteredUser | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  // Forgot Password flow state
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotStep, setForgotStep] = useState<1 | 2>(1);
  const [forgotOtpCode, setForgotOtpCode] = useState(['', '', '', '', '', '']);
  const [forgotOtpSentCode, setForgotOtpSentCode] = useState('');
  const [forgotOtpTimer, setForgotOtpTimer] = useState(30);
  const [forgotOtpNotification, setForgotOtpNotification] = useState(false);
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null);
  const [forgotIsValidating, setForgotIsValidating] = useState(false);
  const [forgotAccountNotFound, setForgotAccountNotFound] = useState(false);
  const [forgotTargetUser, setForgotTargetUser] = useState<RegisteredUser | null>(null);

  // Timer countdown effects
  useEffect(() => {
    let interval: any;
    if (clientStep === 2 && clientOtpTimer > 0) {
      interval = setInterval(() => setClientOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [clientStep, clientOtpTimer]);

  useEffect(() => {
    let interval: any;
    if (companionStep === 2 && compOtpTimer > 0) {
      interval = setInterval(() => setCompOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [companionStep, compOtpTimer]);

  useEffect(() => {
    let interval: any;
    if (loginIsOtpMode && loginOtpTimer > 0) {
      interval = setInterval(() => setLoginOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [loginIsOtpMode, loginOtpTimer]);

  useEffect(() => {
    let interval: any;
    if (isForgotPasswordMode && forgotStep === 2 && forgotOtpTimer > 0) {
      interval = setInterval(() => setForgotOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isForgotPasswordMode, forgotStep, forgotOtpTimer]);

  // OTP inputs ref
  const clientOtpInputs = useRef<(HTMLInputElement | null)[]>([]);
  const compOtpInputs = useRef<(HTMLInputElement | null)[]>([]);

  if (!isOpen) return null;

  // ----------------------------------------------------------------
  // HANDLERS: CLIENT FLOW
  // ----------------------------------------------------------------
  const handleClientSubmitStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    setClientErrorField(null);

    const legalName = clientLegalName.trim() || clientFullName.trim();
    if (!legalName) {
      setClientError('Please enter your Full Legal Name (as per Government ID)');
      setClientErrorField('legalName');
      return;
    }

    const displayName = clientDisplayName.trim() || legalName.split(' ')[0] || 'Member';
    if (!clientDisplayName.trim()) {
      setClientError('Please enter your Profile / Display Name');
      setClientErrorField('displayName');
      return;
    }

    if (!clientEmail.trim() || !clientEmail.includes('@')) {
      setClientError('Please enter a valid email address');
      setClientErrorField('email');
      return;
    }
    if (!clientMobile.trim() || clientMobile.length < 10) {
      setClientError('Please enter a valid 10-digit mobile number');
      setClientErrorField('phone');
      return;
    }

    if (!clientIdNumber.trim()) {
      setClientError(`Please enter your ${clientIdType} number for identity verification`);
      setClientErrorField('idNumber');
      return;
    }

    const clientDobValidation = validateAdultDob(clientDob);
    if (!clientDobValidation.valid) {
      setClientError(clientDobValidation.error || 'You must be at least 18 years old to register.');
      return;
    }

    setClientIsValidating(true);
    try {
      // 1. Email already exists?
      const emailExists = await checkEmailRegistered(clientEmail);
      if (emailExists) {
        setClientError('Email already registered. Please log in or use a different email.');
        setClientErrorField('email');
        return;
      }

      // 2. Phone already exists?
      const phoneExists = await checkPhoneRegistered(clientMobile);
      if (phoneExists) {
        setClientError('Phone number already registered. Please log in or use a different number.');
        setClientErrorField('phone');
        return;
      }

      // Note: Display Name is intentionally not restricted to unique values.
      // Multiple users can have the same Profile / Display Name (e.g. Rahul, Alex, Riya).

      // 3. Password strong enough?
      if (!isPasswordStrong(clientPassword)) {
        setClientError('Password does not meet requirements');
        setClientErrorField('password');
        return;
      }

      // 4. Password = Confirm Password?
      if (clientPassword !== clientConfirmPassword) {
        setClientError('Passwords do not match');
        setClientErrorField('confirmPassword');
        return;
      }

      if (!clientAgreedTerms) {
        setClientError('Please agree to the Terms of Service and Privacy Policy');
        return;
      }

      // Auto verify ID when info provided
      setClientIsIdVerified(true);
      setClientFullName(legalName);

      // CRITICAL ATOMICITY RULE:
      // Do NOT call saveUserToRegistry here!
      // Email and Phone must NOT be registered or reserved until the final account creation step is completely successful.

      // Trigger OTP
      const res = await fetch('/api/v1/auth/registration-otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: clientMobile, mobile: clientMobile })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setClientError(data?.error?.message || data?.message || 'Failed to send OTP. Please check your mobile number.');
        return;
      }
      setClientOtpSentCode(data.verificationId);
      setClientOtpTimer(data.cooldownSeconds || 30);
      playOtpSound();
      setClientStep(2);
    } catch (err: any) {
      setClientError(err?.message || 'Could not reach the verification service. Please try again.');
    } finally {
      setClientIsValidating(false);
    }
  };

  const handleClientVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    const entered = clientOtp.join('');
    if (entered.length < 6) {
      return alert('Please enter the full 6-digit verification code.');
    }
    // Verify OTP code matches
    setClientIsValidating(true);
    fetch('/api/v1/auth/registration-otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: clientMobile, mobile: clientMobile, verificationId: clientOtpSentCode, otp: entered })
    }).then(res => res.json().catch(() => null)).then(data => {
      setClientIsValidating(false);
      if (!data?.success) {
        alert(data?.error?.message || data?.message || 'Invalid OTP verification code.');
        return;
      }
      setClientStep(3);
    }).catch(() => {
      setClientIsValidating(false);
      alert('Error verifying OTP.');
    });
  };

  const handleClientResendOtp = () => {
    if (clientOtpTimer > 0) return;
    fetch('/api/v1/auth/registration-otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: clientMobile, mobile: clientMobile })
    }).then(res => res.json().catch(() => null)).then(data => {
      if (!data?.success) return alert(data?.error?.message || data?.message || 'Failed to resend OTP.');
      setClientOtpSentCode(data.verificationId);
      setClientOtpTimer(data.cooldownSeconds || 30);
      playOtpSound();
    }).catch(() => {
      alert('Could not resend OTP. Please try again.');
    });
  };

  const handleClientSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const dobCheck = validateAdultDob(clientDob);
    if (!dobCheck.valid) {
      alert(dobCheck.error || 'You must be at least 18 years old.');
      return;
    }
    const legalName = clientLegalName.trim() || clientFullName.trim() || 'Valued Member';
    const displayName = clientDisplayName.trim() || legalName.split(' ')[0] || 'Member';

    // Build user profile
    const clientProfileData: Partial<UserProfile> = {
      name: displayName,
      displayName: displayName,
      legalName: legalName,
      phone: clientMobile,
      email: clientEmail,
      avatarUrl: clientPhoto,
      role: 'Verified Client',
      accountType: 'client',
      city: clientCity,
      bio: clientAboutYou,
      hourlyRate: 0,
      isIdVerified: false,
      clientProfile: {
        legalName: legalName,
        displayName: displayName,
        idType: clientIdType,
        idNumber: clientIdNumber,
        isIdVerified: false,
        idDocumentUploaded: clientIdDocUploaded,
        dob: clientDob,
        gender: clientGender,
        preferredLanguage: clientLanguage,
        aboutYou: clientAboutYou,
        lookingFor: clientLookingFor,
        preferredCompanionType: clientCompanionType,
        preferredDuration: clientDuration,
      },
    };

    // EVERYTHING SUCCESSFUL -> CREATE ACCOUNT & PERMANENTLY MARK EMAIL & PHONE AS UNIQUE
    setClientIsValidating(true);
    try {
      let user = auth.currentUser;
      if (!user) {
        const { createUserWithEmailAndPassword } = await import('firebase/auth');
        const credential = await createUserWithEmailAndPassword(
          auth,
          clientEmail.trim(),
          clientPassword
        );
        user = credential.user;
      }

      localStorage.setItem('pairup_user', displayName);
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(clientProfileData));

      // Save permanently to Firestore as completed account
      if (user) {
        await syncUserProfileToFirestore({
          uid: user.uid,
          name: displayName,
          displayName: displayName,
          email: clientEmail,
          phone: clientMobile,
          dob: dobCheck.normalizedDob,
          age: dobCheck.age ?? undefined,
          gender: clientGender,
          city: clientCity,
          accountType: 'client',
          isVerified: false,
          isCompletedRegistration: true,
          avatarUrl: clientPhoto,
          bio: clientAboutYou,
        }).catch(console.warn);
      }

    onLoginSuccess(displayName, 'client');
    setClientStep(4);
    } catch(err: any) { alert(err.message || 'Error creating account'); } finally { setClientIsValidating(false); }
  };

  // ----------------------------------------------------------------
  // HANDLERS: COMPANION FLOW
  // ----------------------------------------------------------------
  const handleCompSubmitStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setCompError(null);
    setCompErrorField(null);

    const legalName = compLegalName.trim() || compFullName.trim();
    if (!legalName) {
      setCompError('Please enter your Full Legal Name (as per Government ID)');
      setCompErrorField('legalName');
      return;
    }

    const displayName = compDisplayName.trim() || legalName.split(' ')[0] || 'Companion Host';
    if (!compDisplayName.trim()) {
      setCompError('Please enter your Profile Name / Display Name');
      setCompErrorField('displayName');
      return;
    }

    if (!compEmail.trim() || !compEmail.includes('@')) {
      setCompError('Please enter a valid email address');
      setCompErrorField('email');
      return;
    }
    if (!compMobile.trim() || compMobile.length < 10) {
      setCompError('Please enter a valid 10-digit mobile number');
      setCompErrorField('phone');
      return;
    }

    const compDobValidation = validateAdultDob(compDob);
    if (!compDobValidation.valid) {
      setCompError(compDobValidation.error || 'Companion must be at least 18 years old.');
      return;
    }
    if (compDobValidation.age !== null) {
      setCompAge(compDobValidation.age);
    }

    setCompIsValidating(true);
    try {
      // 1. Email already exists?
      const emailExists = await checkEmailRegistered(compEmail);
      if (emailExists) {
        setCompError('Email already registered. Please log in or use a different email.');
        setCompErrorField('email');
        return;
      }

      // 2. Phone already exists?
      const phoneExists = await checkPhoneRegistered(compMobile);
      if (phoneExists) {
        setCompError('Phone number already registered. Please log in or use a different number.');
        setCompErrorField('phone');
        return;
      }

      // Multiple users can share the same Display Name (e.g. Rahul, Riya, Alex, Travel Buddy Kolkata).

      // 3. Password strong enough?
      if (!isPasswordStrong(compPassword)) {
        setCompError('Password does not meet requirements');
        setCompErrorField('password');
        return;
      }

      // 4. Password = Confirm Password?
      if (compPassword !== compConfirmPassword) {
        setCompError('Passwords do not match');
        setCompErrorField('confirmPassword');
        return;
      }

      if (!compAgreedTerms) {
        setCompError('Please accept the Terms of Service, Privacy Policy and Companion Guidelines');
        return;
      }

      setCompFullName(legalName);

      // CRITICAL ATOMICITY RULE:
      // Do NOT call saveUserToRegistry here!
      // Email and Phone must NOT be registered or reserved until the final account creation step is completely successful.

      const res = await fetch('/api/v1/auth/registration-otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: compMobile, mobile: compMobile })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setCompError(data?.error?.message || data?.message || 'Failed to send OTP. Please check your mobile number.');
        return;
      }
      setCompOtpSentCode(data.verificationId);
      setCompOtpTimer(data.cooldownSeconds || 30);
      playOtpSound();
      setCompanionStep(2);
    } catch (err: any) {
      setCompError(err?.message || 'Could not reach the verification service. Please try again.');
    } finally {
      setCompIsValidating(false);
    }
  };

  const handleCompVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    const entered = compOtp.join('');
    if (entered.length < 6) {
      return alert('Please enter the full 6-digit OTP code.');
    }
    // Verify OTP code matches
    setCompIsValidating(true);
    fetch('/api/v1/auth/registration-otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: compMobile, mobile: compMobile, verificationId: compOtpSentCode, otp: entered })
    }).then(res => res.json().catch(() => null)).then(data => {
      setCompIsValidating(false);
      if (!data?.success) {
        alert(data?.error?.message || data?.message || 'Invalid OTP verification code.');
        return;
      }
      setCompanionStep(3);
    }).catch(() => {
      setCompIsValidating(false);
      alert('Error verifying OTP.');
    });
  };

  const handleCompResendOtp = () => {
    if (compOtpTimer > 0) return;
    fetch('/api/v1/auth/registration-otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: compMobile, mobile: compMobile })
    }).then(res => res.json().catch(() => null)).then(data => {
      if (!data?.success) return alert(data?.error?.message || data?.message || 'Failed to resend OTP.');
      setCompOtpSentCode(data.verificationId);
      setCompOtpTimer(data.cooldownSeconds || 30);
      playOtpSound();
    }).catch(() => {
      alert('Could not resend OTP. Please try again.');
    });
  };

  const handleCompSubmitIdentity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!compIdNumber.trim()) return alert('Please enter your Government ID Number');
    if (!compIdDocUploaded) return alert('Please upload your ID document');
    if (!compSelfieUploaded) return alert('Please take or upload your verification selfie');
    if (!compAgreedIdentityAccuracy) return alert('Please confirm that the ID information is accurate');

    setCompVerificationInProgress(true);
    // Proceed to Profile Setup (Step 4) while verification is reviewed
    setTimeout(() => {
      setCompanionStep(4);
    }, 600);
  };

  const handleCompSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!compDisplayName.trim()) return alert('Please enter your Display Name');
    const dobCheck = validateAdultDob(compDob);
    if (!dobCheck.valid) return alert(dobCheck.error || 'Companion must be at least 18 years old.');
    if (dobCheck.age !== null) setCompAge(dobCheck.age);
    if (!compHourlyRate || compHourlyRate < 300) return alert('Minimum hourly rate is ₹300/hour');
    setCompanionStep(5); // Go to Choose Membership Plan
  };

  const handleCompChoosePlan = async (plan: {
    id: CompanionMembershipPlan;
    name: string;
    price: number;
    period: string;
  }) => {
    setSelectedPlan(plan);
    setPaymentVerificationStatus('IDLE');
    setPaymentErrorMessage(null);
    setPaymentFailedState(false);
    setCurrentOrderId('');
    setCurrentOrderKeyId('');
    setCurrentOrderAmountPaise(0);

    try {
      // Firebase identity is required; never use an email-derived fake UID.
      let user = auth.currentUser;
      if (!user) {
        const credential = await createUserWithEmailAndPassword(
          auth,
          compEmail.trim(),
          compPassword
        );
        user = credential.user;
      }

      await user.getIdToken(true);

      const order = await createPaymentOrder({
        companionId: user.uid,
        planId: plan.id,
        planName: plan.name,
        amount: plan.price,
        email: compEmail.trim(),
        phone: compMobile,
      });

      const orderId = String(order.orderId || '');
      const keyId = String(order.keyId || '');
      const amountPaise = Number(order.amount);

      if (
        !orderId ||
        !keyId ||
        !Number.isSafeInteger(amountPaise) ||
        amountPaise <= 0
      ) {
        throw new Error(
          'Backend did not return a valid Razorpay order, public key, and amount. Registration remains incomplete.'
        );
      }

      setCurrentOrderId(orderId);
      setCurrentOrderKeyId(keyId);
      setCurrentOrderAmountPaise(amountPaise);
      setCompanionStep(6);
    } catch (error: any) {
      setPaymentVerificationStatus('FAILED');
      setPaymentFailedState(true);
      setPaymentErrorMessage(
        error?.message ||
        'Secure payment order could not be created. Registration remains incomplete.'
      );
      setCompanionStep(5);
      alert(
        error?.message ||
        'Secure payment order could not be created. Please try again.'
      );
    }
  };

  const handleCompCompletePayment = async (
    response: RazorpayCheckoutResponse
  ) => {
    if (!selectedPlan || !currentOrderId) {
      setPaymentVerificationStatus('FAILED');
      setPaymentFailedState(true);
      setPaymentErrorMessage('A valid backend payment order is required.');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setPaymentVerificationStatus('FAILED');
      setPaymentFailedState(true);
      setPaymentErrorMessage('Your authenticated session is missing. Please sign in again.');
      return;
    }

    if (
      !response?.razorpay_payment_id ||
      !response?.razorpay_order_id ||
      !response?.razorpay_signature ||
      response.razorpay_order_id !== currentOrderId
    ) {
      setPaymentVerificationStatus('FAILED');
      setPaymentFailedState(true);
      setPaymentErrorMessage('Razorpay returned incomplete or mismatched payment details.');
      return;
    }

    setIsProcessingCheckout(true);
    setPaymentErrorMessage(null);
    setPaymentFailedState(false);

    try {
      await currentUser.getIdToken(true);
      const verification = await verifyPaymentWithBackend({
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        signature: response.razorpay_signature,
        companionId: currentUser.uid,
        planId: selectedPlan.id,
      });

      setIsProcessingCheckout(false);

    // 2. CHECK BACKEND VERIFICATION RESULT
      if (!verification.verified || verification.payment_status !== 'Success' || verification.payment_verified !== true || verification.webhook_verified !== 'YES' || !verification.paymentId) {
        // PAYMENT FAILED OR CANCELLED -> REGISTRATION INCOMPLETE
        setPaymentVerificationStatus('FAILED');
        setPaymentFailedState(true);
        setPaymentErrorMessage(verification.error || 'Payment failed or cancelled. Registration incomplete.');

        // CRITICAL ATOMICITY RULE:
        // Do NOT save user to registry!
        // Do NOT store in pairup_user_profile_data or Firestore!
        // Email and phone remain 100% free and reusable if the user restarts signup!
        return;
      }

      // 3. PAYMENT VERIFIED BY BACKEND GATEWAY -> REGISTRATION SUCCESS & ACTIVATION!
      // ATOMIC COMMIT: Mark Email and Phone permanently UNIQUE
      const legalName = compLegalName.trim() || compFullName.trim();
      const displayName = compDisplayName.trim() || legalName.split(' ')[0] || 'Companion Host';

      if (!legalName || !compDisplayName.trim()) {
        throw new Error('Full legal name and profile display name are required.');
      }

      const compMembership: MembershipSubscription = {
        plan: selectedPlan.id,
        price: selectedPlan.price,
        status: 'ACTIVE',
        nextBillingDate: nextBillingDate,
        paymentHistory: [
          {
            id: verification.paymentId,
            date: 'Today',
            amount: selectedPlan.price,
            plan: selectedPlan.name,
            status: 'PAID',
          },
        ],
      };

      const activeProfileData: Partial<UserProfile> = {
        name: displayName,
        displayName: displayName,
        legalName: legalName,
        phone: compMobile,
        email: compEmail,
        avatarUrl: compPhoto,
        role: 'Companion Host & Member',
        accountType: 'companion',
        companionStatus: 'ACTIVE',
        companion_profile_status: 'Active',
        membership_status: 'ACTIVE',
        payment_status: 'Success',
        payment_verified: true,
        isIdVerified: false,
        city: compCity,
        bio: compAboutMe,
        hourlyRate: compHourlyRate,
        membership: compMembership,
        companionProfile: {
          legalName: legalName,
          displayName: displayName,
          dob: compDob,
          age: compAge,
          gender: compGender,
          city: compCity,
          languages: compLanguages.split(',').map((s) => s.trim()),
          aboutMe: compAboutMe,
          interests: compInterests.split(',').map((s) => s.trim()),
          skills: compSkills.split(',').map((s) => s.trim()),
          experience: compExperience,
          services: compServices,
          hourlyRate: compHourlyRate,
          minDuration: compMinDuration,
          availability: compAvailability,
          idType: compIdType,
          idNumber: compIdNumber,
          idDocumentUrl: compIdDocUrl || undefined,
          selfieUrl: compSelfieUrl || undefined,
        },
      };

      // Fail closed: Firestore activation must complete before local success state.
      // Do not silently continue if the authenticated session disappears.
      if (!auth.currentUser || auth.currentUser.uid !== currentUser.uid) {
        throw new Error('Authenticated session changed. Registration remains incomplete.');
      }

      await syncUserProfileToFirestore({
        uid: currentUser.uid,
        name: displayName,
        displayName: displayName,
        email: compEmail,
        phone: compMobile,
        dob: compDob,
        age: compAge,
        gender: compGender,
        city: compCity,
        accountType: 'companion',
        isVerified: false,
        isCompletedRegistration: true,
        avatarUrl: compPhoto,
        hourlyRate: compHourlyRate,
        bio: compAboutMe,
        companionStatus: 'ACTIVE',
        companion_profile_status: 'Active',
        membership_status: 'ACTIVE',
        payment_status: 'Success',
        payment_verified: true,
        membershipPlan: selectedPlan.id,
        membershipStatus: 'ACTIVE',
        nextBillingDate: nextBillingDate,
      });

      // Commit browser state only after the awaited Firestore write succeeds.
      localStorage.setItem('pairup_user', displayName);
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(activeProfileData));


      setPaymentVerificationStatus('SUCCESS');
      onLoginSuccess(displayName, 'companion', activeProfileData, compMembership);
      setCompanionStep(7); // Payment Success Screen
    } catch (err: any) {
      setIsProcessingCheckout(false);
      setPaymentVerificationStatus('FAILED');
      setPaymentFailedState(true);
      setPaymentErrorMessage(err.message || 'Payment verification error.');
    }
  };

  /**
   * Companion skips payment or chooses to activate later:
   * Profile is saved as strictly INACTIVE.
   * Prompts "Complete Payment to Activate Profile" in companion dashboard.
   */
  const handleCompSkipPayment = () => {
    setPaymentVerificationStatus('FAILED');
    setPaymentFailedState(true);
    setPaymentErrorMessage('Payment is required to complete Companion registration.');
  };

  // ----------------------------------------------------------------
  // HANDLERS: LOGIN (Spec Section 16 & Strict Login Rule)
  // ----------------------------------------------------------------
  const handleStandardLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginAccountNotFound(false);
    setLoginPasswordIncorrect(false);

    const cleanPhone = loginIdentifier.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setLoginError('Please enter a valid 10-digit Indian mobile number (e.g. 9876543210)');
      return;
    }
    if (!loginPassword.trim()) {
      setLoginError('Please enter your password');
      return;
    }

    setLoginIsValidating(true);
    try {
      // 1. Check if user has an account
      const check = await checkAccountExists(cleanPhone);
      let loginEmail = (check.user?.email || '').trim().toLowerCase();

      if (!loginEmail) {
        try {
          const res = await fetch('/api/v1/auth/resolve-mobile-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mobile: cleanPhone }),
          });
          const data = await res.json();
          if (res.ok && data?.success && data?.email) {
            loginEmail = String(data.email).trim().toLowerCase();
          }
        } catch {}
      }

      if (loginEmail) {
        try {
          await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
        } catch (authErr: any) {
          if (authErr?.code === 'auth/user-not-found') {
            setLoginAccountNotFound(true);
            setLoginError('No account found with this mobile number. Please sign up first.');
            return;
          }
          setLoginPasswordIncorrect(true);
          setLoginError('Incorrect password. Please try again.');
          return;
        }
      } else {
        if (!check.exists) {
          setLoginAccountNotFound(true);
          setLoginError('No account found with this mobile number. Please sign up first.');
          return;
        }
        setLoginError('Please use "Continue with OTP" to sign in with your mobile number.');
        return;
      }

      // 3. Password verified -> Login
      setLoginPasswordIncorrect(false);
      const userName =
        check.user?.name ||
        `Member_${cleanPhone.slice(-4)}`;
      const accountType = check.user?.accountType || 'client';

      localStorage.setItem('pairup_user', userName);
      onLoginSuccess(
        userName,
        accountType,
        check.user
          ? {
              name: userName,
              email: check.user.email || loginEmail,
              phone: check.user.phone || cleanPhone,
              accountType: accountType,
              companionStatus: check.user.companionStatus,
            }
          : undefined
      );

      // Redirect according to stored role
      if (accountType === 'companion') {
        onOpenCompanionDashboard?.();
      } else {
        onOpenClientDashboard?.();
      }
      onClose();
    } finally {
      setLoginIsValidating(false);
    }
  };

  const handleLoginWithOtpRequest = async () => {
    setLoginError(null);
    setLoginAccountNotFound(false);
    setLoginPasswordIncorrect(false);

    const cleanPhone = loginIdentifier.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setLoginError('Please enter a valid 10-digit Indian mobile number to log in with SMS OTP');
      return;
    }

    setLoginIsValidating(true);
    try {
      const check = await checkAccountExists(cleanPhone).catch(() => ({ exists: false, user: undefined }));
      setLoginOtpTargetUser(check.user || null);

      const res = await fetch('/api/v1/auth/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone })
      });
      const data = await res.json();
      if (!data.success) {
        if (res.status === 404 || data.error?.code === 'ACCOUNT_NOT_FOUND') {
          setLoginAccountNotFound(true);
        }
        setLoginError(data.error?.message || 'Failed to send OTP.');
        return;
      }
      setLoginOtpSentCode(data.verificationId);
      setLoginOtpCode(['', '', '', '', '', '']);
      setLoginOtpTimer(data.cooldownSeconds || 30);
      setLoginIsOtpMode(true);
      setLoginOtpSent(true);
      playOtpSound();
    } finally {
      setLoginIsValidating(false);
    }
  };

  const handleLoginResendOtp = () => {
    if (loginOtpTimer > 0) return;
    const cleanPhone = loginIdentifier.replace(/\D/g, '').slice(-10);
    fetch('/api/v1/auth/otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone })
    }).then(res => res.json()).then(data => {
      if (!data.success) {
        setLoginError(data.error?.message || 'Failed to resend OTP.');
        return;
      }
      setLoginOtpSentCode(data.verificationId);
      setLoginOtpTimer(data.cooldownSeconds || 30);
      playOtpSound();
    });
  };

  const handleLoginOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    const entered = loginOtpCode.join('');
    if (entered.length < 6) {
      setLoginError('Please enter the 6-digit OTP code');
      return;
    }

    setLoginIsValidating(true);
    try {
      const cleanPhone = loginIdentifier.replace(/\D/g, '').slice(-10);
      const res = await fetch('/api/v1/auth/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: cleanPhone,
          mobile: cleanPhone,
          verificationId: loginOtpSentCode,
          otp: entered
        })
      });
      const data = await res.json();
      if (!data.success) {
        setLoginError(data.error?.message || 'Invalid OTP code.');
        return;
      }

      // Log in with custom token using imported Firebase auth instance
      const { signInWithCustomToken } = await import('firebase/auth');
      await signInWithCustomToken(auth, data.customToken);

      const serverUser = data.user;
      const userName =
        serverUser?.fullName ||
        loginOtpTargetUser?.name ||
        `Member_${cleanPhone.slice(-4) || 'VIP'}`;
      const accountType: 'client' | 'companion' =
        serverUser?.role === 'companion' || loginOtpTargetUser?.accountType === 'companion'
          ? 'companion'
          : 'client';

      localStorage.setItem('pairup_user', userName);
      onLoginSuccess(userName, accountType, {
        name: userName,
        email: serverUser?.email || loginOtpTargetUser?.email || '',
        phone: serverUser?.phone || loginOtpTargetUser?.phone || cleanPhone,
        accountType,
        companionStatus: loginOtpTargetUser?.companionStatus,
      });

      if (accountType === 'companion') {
        onOpenCompanionDashboard?.();
      } else {
        onOpenClientDashboard?.();
      }
      onClose();
    } catch (err: any) {
      setLoginError(err.message || 'Error verifying OTP');
    } finally {
      setLoginIsValidating(false);
    }
  };

  // ----------------------------------------------------------------
  // HANDLERS: FORGOT PASSWORD FLOW
  // ----------------------------------------------------------------
  const handleForgotSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    setForgotAccountNotFound(false);

    const cleanPhone = forgotIdentifier.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setForgotError('Please enter your registered 10-digit Indian mobile number.');
      return;
    }

    setForgotIsValidating(true);
    try {
      const res = await fetch('/api/v1/auth/password-reset/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        if (res.status === 404 || data?.error?.code === 'ACCOUNT_NOT_FOUND') {
          setForgotAccountNotFound(true);
        }
        setForgotError(data?.error?.message || 'Could not send password reset OTP. Ensure the mobile number is registered.');
        return;
      }

      setForgotOtpSentCode(data.verificationId);
      setForgotOtpCode(['', '', '', '', '', '']);
      setForgotOtpTimer(data.cooldownSeconds || 30);
      setForgotStep(2);
      playOtpSound();
    } catch (err: any) {
      setForgotError(err.message || 'Error sending password reset request. Ensure the account is registered.');
    } finally {
      setForgotIsValidating(false);
    }
  };

  const handleForgotResendOtp = () => {
    if (forgotOtpTimer > 0) return;
    const cleanPhone = forgotIdentifier.trim().replace(/\D/g, '');
    fetch('/api/v1/auth/password-reset/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone }),
    })
      .then((res) => res.json().catch(() => null))
      .then((data) => {
        if (!data?.success) {
          setForgotError(data?.error?.message || 'Failed to resend OTP.');
          return;
        }
        setForgotOtpSentCode(data.verificationId);
        setForgotOtpTimer(data.cooldownSeconds || 30);
        playOtpSound();
      });
  };

  const handleForgotResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError(null);

    const enteredOtp = forgotOtpCode.join('');
    if (enteredOtp.length < 6) {
      setForgotError('Please enter the 6-digit OTP code');
      return;
    }
    if (!forgotOtpSentCode) {
      setForgotError('Please request a verification code first.');
      return;
    }
    if (!isPasswordStrong(forgotNewPassword)) {
      setForgotError('New password must be at least 8 chars with letters and a number/symbol');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('New passwords do not match');
      return;
    }

    setForgotIsValidating(true);
    try {
      const cleanPhone = forgotIdentifier.trim().replace(/\D/g, '');
      const res = await fetch('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: cleanPhone,
          mobile: cleanPhone,
          verificationId: forgotOtpSentCode,
          otp: enteredOtp,
          newPassword: forgotNewPassword,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        setForgotError(data?.error?.message || 'Incorrect or expired OTP code. Please try again.');
        return;
      }

      setForgotSuccess('Password reset successfully! You can now log in with your new password.');
      setLoginIdentifier(forgotIdentifier);
      setLoginPassword(forgotNewPassword);
      setLoginPasswordIncorrect(false);
      setLoginError(null);

      setTimeout(() => {
        setIsForgotPasswordMode(false);
        setForgotStep(1);
        setForgotSuccess(null);
      }, 1500);
    } catch (err: any) {
      setForgotError(err.message || 'Could not reset password. Please try again.');
    } finally {
      setForgotIsValidating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-pink-100 max-h-[94vh] flex flex-col">

          {/* Modal Top Bar */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70 shrink-0">
          <div className="flex items-center gap-2.5">
            <PairUpLogo className="w-10 h-10 shrink-0" />
            <span className="font-black text-slate-900 tracking-tight text-sm sm:text-base">
              PairUp Today
            </span>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            <button
              onClick={onClose}
              className="p-1.5 rounded-full bg-slate-200/80 hover:bg-slate-300 text-slate-700 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body Container */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-8">

          {/* ========================================================================= */}
          {/* VIEW 1: MAIN SIGNUP CHOICE (SPEC SECTION 1)                               */}
          {/* ========================================================================= */}
          {currentMode === 'signup-choice' && (
            <div className="space-y-6">
              <div className="flex justify-center mb-2">
                <PairUpLogo className="w-32 h-32" />
              </div>
              <div className="text-center max-w-md mx-auto">
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
                  Create Your PairUp Today Account
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1.5">
                  Choose how you want to use PairUp Today.
                </p>
              </div>

              {/* Two Account Types Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">

                {/* CARD 1: CLIENT */}
                <div className="p-6 rounded-3xl border border-slate-200 hover:border-pink-300 bg-white hover:bg-pink-50/20 transition-all flex flex-col justify-between shadow-xs group">
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-pink-100 text-pink-600 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <User className="w-6 h-6" />
                    </div>

                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xl font-black text-slate-900">👤 Client</h3>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-full uppercase">
                        Free
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mb-5">
                      Find the right companion for your needs.
                    </p>

                    <ul className="space-y-2.5 text-xs text-slate-600 mb-6">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                        <span>Browse companion profiles</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                        <span>Send booking requests</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                        <span>Manage your bookings</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                        <span>Secure payments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-pink-600 shrink-0 mt-0.5" />
                        <span>Rate and review companions</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setClientStep(1);
                      setCurrentMode('client-signup');
                    }}
                    className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>Continue as Client</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* CARD 2: COMPANION */}
                <div className="p-6 rounded-3xl border border-purple-200 hover:border-purple-400 bg-white hover:bg-purple-50/20 transition-all flex flex-col justify-between shadow-xs group">
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <Heart className="w-6 h-6" />
                    </div>

                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xl font-black text-slate-900">🤝 Companion</h3>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-black rounded-full uppercase">
                        Earn ₹
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mb-5">
                      Become a companion and earn by accepting bookings.
                    </p>

                    <ul className="space-y-2.5 text-xs text-slate-600 mb-6">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>Create your companion profile</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>Set your own hourly rate</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>Accept or reject bookings</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>Earn from every booking</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>Flexible working hours</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        <span>AI-powered client matching</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setCompanionStep(1);
                      setCurrentMode('companion-signup');
                    }}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>Continue as Companion</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

              </div>

              {/* Below buttons: Already have an account? Log In */}
              <div className="pt-4 text-center border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setCurrentMode('login')}
                    className="font-bold text-pink-600 hover:text-pink-700 underline cursor-pointer ml-1"
                  >
                    Log In
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* VIEW 2: CLIENT SIGNUP FLOW (SPEC SECTION 2)                               */}
          {/* ========================================================================= */}
          {currentMode === 'client-signup' && (
            <div className="space-y-6">
              {/* Back to Choice */}
              {clientStep === 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentMode('signup-choice')}
                  className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Account Types
                </button>
              )}

              {/* Step indicator */}
              <div className="flex items-center justify-between text-xs font-bold text-slate-400">
                <span>Client Registration</span>
                <span>Step {clientStep} of 4</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-pink-600 h-full transition-all duration-300"
                  style={{ width: `${(clientStep / 4) * 100}%` }}
                />
              </div>

              {/* STEP 1: CREATE CLIENT ACCOUNT */}
              {clientStep === 1 && (
                <form onSubmit={handleClientSubmitStep1} className="space-y-4">
                  <div className="text-center max-w-sm mx-auto">
                    <h3 className="text-2xl font-black text-slate-900">Create Your Client Account</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Join free to browse verified companions and send booking requests.
                    </p>
                  </div>

                  {/* Top Error Alert if any validation failed */}
                  {clientError && (
                    <div
                      id="client-signup-error-banner"
                      className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700 flex items-start gap-2 shadow-xs"
                    >
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p>{clientError}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3.5 text-xs">
                    {/* Legal Name vs Display Name Guidance Banner */}
                    <div className="p-3 bg-gradient-to-r from-pink-50 to-slate-50 border border-pink-100 rounded-2xl text-[11px] space-y-1">
                      <div className="flex items-center gap-1.5 font-bold text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-pink-600 shrink-0" />
                        <span>Identity Privacy & Verified Badge</span>
                      </div>
                      <p className="text-slate-600 leading-relaxed">
                        Others on PairUp Today only see your <strong>Profile Name / Display Name</strong> with a{' '}
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-bold text-sky-700 bg-sky-100 border border-sky-200">
                          Verified ✓
                        </span>{' '}
                        badge. Your <strong>Full Legal Name</strong> and Government ID are strictly private for compliance and security.
                      </p>
                    </div>

                    {/* Full Legal Name (Government ID অনুযায়ী) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-800 font-bold">
                          Full Legal Name <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                          <Lock className="w-3 h-3 text-slate-400" /> Government ID অনুযায়ী (Private)
                        </span>
                      </div>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Rahul Kumar Sharma"
                        value={clientLegalName}
                        onChange={(e) => {
                          setClientLegalName(e.target.value);
                          if (!clientDisplayName) {
                            // Suggest first name
                            const first = e.target.value.trim().split(' ')[0];
                            if (first) setClientDisplayName(first);
                          }
                          if (clientErrorField === 'legalName') {
                            setClientError(null);
                            setClientErrorField(null);
                          }
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                          clientErrorField === 'legalName'
                            ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                            : 'border-slate-300 focus:border-pink-500'
                        }`}
                      />
                      <p className="text-[10px] text-slate-400 mt-1">
                        🔒 Stored strictly for identity verification, payment, & compliance. Never shown on your public profile.
                      </p>
                    </div>

                    {/* Profile Name / Display Name (নিজের পছন্দমতো) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-800 font-bold">
                          Profile Name / Display Name <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[10px] font-semibold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full border border-pink-200">
                          👉 Publicly Visible
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          placeholder="e.g. Rahul, Alex, Riya, Travel Buddy"
                          value={clientDisplayName}
                          onChange={(e) => {
                            setClientDisplayName(e.target.value);
                            if (clientErrorField === 'displayName') {
                              setClientError(null);
                              setClientErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 pr-24 rounded-xl border focus:outline-none transition-colors ${
                            clientErrorField === 'displayName'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-pink-500'
                          }`}
                        />
                        {clientDisplayName && (
                          <div className="absolute right-2.5 top-2.5 flex items-center gap-1 bg-sky-50 text-sky-700 px-2 py-0.5 rounded-md border border-sky-200 text-[10px] font-bold">
                            <CheckCircle2 className="w-3 h-3 text-sky-600" />
                            <span>Verified ✓</span>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        ✨ নিজের পছন্দমতো নাম। Multiple people can choose the same display name.
                      </p>
                    </div>

                    {/* Email Address (Unique) */}
                    <div>
                      <label className="block text-slate-800 font-bold mb-1">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="rahul@example.com"
                        value={clientEmail}
                        onChange={(e) => {
                          setClientEmail(e.target.value);
                          if (clientErrorField === 'email') {
                            setClientError(null);
                            setClientErrorField(null);
                          }
                        }}
                        onBlur={async () => {
                          if (clientEmail.trim() && clientEmail.includes('@')) {
                            const exists = await checkEmailRegistered(clientEmail);
                            if (exists) {
                              setClientError('Email already exists. Please log in or use a different email.');
                              setClientErrorField('email');
                            }
                          }
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                          clientErrorField === 'email'
                            ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                            : 'border-slate-300 focus:border-pink-500'
                        }`}
                      />
                      {clientErrorField === 'email' ? (
                        <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          Email already exists. Please log in or use a different email.
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400 mt-1">Must be unique to your account.</p>
                      )}
                    </div>

                    {/* Mobile Number (Unique) */}
                    <div>
                      <label className="block text-slate-800 font-bold mb-1">
                        Phone Number <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-2.5 text-slate-400 font-medium">+91</span>
                        <input
                          type="tel"
                          required
                          maxLength={10}
                          placeholder="98765 43210"
                          value={clientMobile}
                          onChange={(e) => {
                            setClientMobile(e.target.value.replace(/\D/g, ''));
                            if (clientErrorField === 'phone') {
                              setClientError(null);
                              setClientErrorField(null);
                            }
                          }}
                          onBlur={async () => {
                            if (clientMobile.trim().length === 10) {
                              const exists = await checkPhoneRegistered(clientMobile);
                              if (exists) {
                                setClientError('Phone number already exists. Please log in or use a different number.');
                                setClientErrorField('phone');
                              }
                            }
                          }}
                          className={`w-full pl-12 pr-3.5 py-2.5 rounded-xl border focus:outline-none font-mono transition-colors ${
                            clientErrorField === 'phone'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-pink-500'
                          }`}
                        />
                      </div>
                      {clientErrorField === 'phone' ? (
                        <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          Phone number already exists. Please log in or use a different number.
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400 mt-1">Used for instant SMS login & verification OTP.</p>
                      )}
                    </div>

                    {/* Date of Birth (18+ Enforced) */}
                    <DateOfBirthInput
                      id="auth-modal-client-step1-dob"
                      value={clientDob}
                      onChange={(nextDob) => {
                        setClientDob(nextDob);
                        if (clientError) setClientError(null);
                      }}
                      variant="light"
                    />

                    {/* Passwords */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">
                          Password <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="password"
                          required
                          placeholder="Min. 8 characters"
                          value={clientPassword}
                          onChange={(e) => {
                            setClientPassword(e.target.value);
                            if (clientErrorField === 'password') {
                              setClientError(null);
                              setClientErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                            clientErrorField === 'password'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-pink-500'
                          }`}
                        />
                        {clientErrorField === 'password' ? (
                          <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            Password does not meet requirements
                          </p>
                        ) : (
                          <p className="text-[10px] text-slate-400 mt-1">
                            8+ characters with letters & numbers/symbols
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">
                          Confirm Password <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="password"
                          required
                          placeholder="Re-enter password"
                          value={clientConfirmPassword}
                          onChange={(e) => {
                            setClientConfirmPassword(e.target.value);
                            if (clientErrorField === 'confirmPassword') {
                              setClientError(null);
                              setClientErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                            clientErrorField === 'confirmPassword'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-pink-500'
                          }`}
                        />
                        {clientErrorField === 'confirmPassword' ? (
                          <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            Passwords do not match
                          </p>
                        ) : clientConfirmPassword && clientPassword === clientConfirmPassword ? (
                          <p className="text-[11px] text-emerald-600 font-bold mt-1 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5 shrink-0" />
                            Passwords match
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {/* Government ID Verification Section */}
                    <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center">
                            <ShieldCheck className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <span className="font-bold text-slate-900 block text-xs">Government ID Verification</span>
                            <span className="text-[10px] text-slate-500">
                              Enables your public <span className="font-bold text-sky-600">Verified ✓</span> badge
                            </span>
                          </div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold border border-emerald-200">
                          100% Private
                        </span>
                      </div>

                      {/* ID Type Selector */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">Select ID Document Type</label>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                          {['Aadhaar Card', 'Passport', 'Voter ID', 'Driving License', 'PAN Card'].map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => setClientIdType(type)}
                              className={`px-2 py-1.5 rounded-lg text-center font-bold text-[10px] transition-all cursor-pointer truncate ${
                                clientIdType === type
                                  ? 'bg-pink-600 text-white shadow-xs'
                                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* ID Number */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          {clientIdType} Number <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder={
                            clientIdType === 'Aadhaar Card'
                              ? 'e.g. 5482 9201 4829'
                              : clientIdType === 'Passport'
                              ? 'e.g. Z9182734'
                              : 'Enter ID document number'
                          }
                          value={clientIdNumber}
                          onChange={(e) => {
                            setClientIdNumber(e.target.value);
                            if (clientErrorField === 'idNumber') {
                              setClientError(null);
                              setClientErrorField(null);
                            }
                          }}
                          className={`w-full px-3 py-2 rounded-xl border bg-white focus:outline-none font-mono uppercase text-xs transition-colors ${
                            clientErrorField === 'idNumber'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-pink-500'
                          }`}
                        />
                      </div>

                      {/* Document Upload Button / Verification */}
                      <div className="flex items-center justify-between pt-1 gap-2">
                        <div className="text-[10px] text-slate-500 flex items-center gap-1.5 min-w-0">
                          {clientIdDocUploaded ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          ) : (
                            <Upload className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          )}
                          <span className="truncate">
                            {clientIdDocUploaded
                              ? `${clientIdType} Document Attached ✓`
                              : `Upload ${clientIdType} (Camera / File)`}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsClientDocModalOpen(true)}
                          className={`px-2.5 py-1 text-[11px] font-bold rounded-lg cursor-pointer transition-colors shrink-0 ${
                            clientIdDocUploaded
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-white border border-slate-300 text-slate-700 hover:border-pink-500'
                          }`}
                        >
                          {clientIdDocUploaded ? 'Change Document' : 'Browse / Camera'}
                        </button>
                      </div>
                    </div>

                    <div className="pt-1">
                      <label className="flex items-start gap-2 cursor-pointer text-slate-600 text-xs">
                        <input
                          type="checkbox"
                          checked={clientAgreedTerms}
                          onChange={(e) => setClientAgreedTerms(e.target.checked)}
                          className="w-4 h-4 rounded accent-pink-600 mt-0.5 cursor-pointer"
                        />
                        <span>I agree to the Terms of Service and Privacy Policy</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={clientIsValidating}
                    className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-75"
                  >
                    {clientIsValidating ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Validating Account...</span>
                      </>
                    ) : (
                      <span>Create Free Account</span>
                    )}
                  </button>

                  <p className="text-[11px] text-slate-500 text-center">
                    Client registration is free. No membership fee is required.
                  </p>
                </form>
              )}

              {/* STEP 2: CLIENT MOBILE VERIFICATION */}
              {clientStep === 2 && (
                <form onSubmit={handleClientVerifyOtp} className="space-y-5 text-center max-w-sm mx-auto">
                  <div className="w-12 h-12 rounded-2xl bg-pink-100 text-pink-600 flex items-center justify-center mx-auto">
                    <Smartphone className="w-6 h-6" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-black text-slate-900">Verify Your Mobile Number</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      We've sent a 6-digit verification code to <strong className="text-slate-800">+91 {clientMobile || 'XXXXX XXXXX'}</strong>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-2">Enter OTP</label>
                    <div className="flex justify-center gap-2">
                      {clientOtp.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={(el) => {
                            clientOtpInputs.current[idx] = el;
                          }}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            const next = [...clientOtp];
                            next[idx] = val;
                            setClientOtp(next);
                            if (val && idx < 5) {
                              clientOtpInputs.current[idx + 1]?.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !digit && idx > 0) {
                              clientOtpInputs.current[idx - 1]?.focus();
                            }
                          }}
                          className="w-11 h-12 text-center font-mono font-bold text-lg border border-slate-300 rounded-xl focus:border-pink-500 focus:outline-none bg-slate-50"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      type="submit"
                      className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                    >
                      Verify & Continue
                    </button>

                    <button
                      type="button"
                      disabled={clientOtpTimer > 0}
                      onClick={handleClientResendOtp}
                      className="text-xs font-bold text-slate-600 hover:text-slate-900 disabled:opacity-50 cursor-pointer py-1"
                    >
                      {clientOtpTimer > 0 ? `Resend OTP in ${clientOtpTimer} seconds` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 3: CLIENT PROFILE */}
              {clientStep === 3 && (
                <form onSubmit={handleClientSaveProfile} className="space-y-4">
                  <div className="text-center max-w-sm mx-auto">
                    <h3 className="text-2xl font-black text-slate-900">Complete Your Profile</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Help companions know your preferences for smooth, enjoyable meetups.
                    </p>
                  </div>

                  <div className="space-y-3 text-xs">
                    {/* Profile Photo */}
                    <div className="flex items-center gap-4 p-3 rounded-2xl bg-slate-50 border border-slate-200">
                      <img
                        src={clientPhoto}
                        alt="Profile"
                        className="w-14 h-14 rounded-2xl object-cover border-2 border-pink-400"
                      />
                      <div>
                        <span className="font-bold text-slate-800 block">Profile Photo</span>
                        <div className="flex gap-2 mt-1">
                          {[
                            'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80',
                            'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?auto=format&fit=crop&w=400&q=80',
                            'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
                          ].map((url, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setClientPhoto(url)}
                              className="text-[10px] px-2 py-0.5 rounded bg-white border border-slate-300 hover:border-pink-500 font-medium"
                            >
                              Preset {i + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <DateOfBirthInput
                          id="auth-modal-client-step3-dob"
                          value={clientDob}
                          onChange={(nextDob) => setClientDob(nextDob)}
                          variant="light"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Gender</label>
                        <select
                          value={clientGender}
                          onChange={(e) => setClientGender(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white"
                        >
                          <option>Male</option>
                          <option>Female</option>
                          <option>Non-binary</option>
                          <option>Prefer not to say</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1 flex items-center justify-between">
                          <span>City</span>
                          <span className="text-[10px] text-pink-600 font-normal">Google Places (IN)</span>
                        </label>
                        <DynamicLocationSearchBox
                          placeholder="Search your city"
                          selectedLocation={
                            clientCity
                              ? {
                                  place_id: `place_${clientCity}`,
                                  formatted_address: clientCity,
                                  latitude: 22.58,
                                  longitude: 88.46,
                                  city: clientCity,
                                  country: 'India',
                                  country_code: 'IN',
                                }
                              : null
                          }
                          onLocationSelect={(loc) => {
                            if (loc) {
                              setClientCity(loc.formatted_address || loc.city || '');
                            }
                          }}
                          size="sm"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Preferred Language</label>
                        <input
                          type="text"
                          value={clientLanguage}
                          onChange={(e) => setClientLanguage(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-slate-700 font-bold mb-1">About You</label>
                      <textarea
                        rows={2}
                        value={clientAboutYou}
                        onChange={(e) => setClientAboutYou(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-300"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">What are you looking for?</label>
                        <input
                          type="text"
                          value={clientLookingFor}
                          onChange={(e) => setClientLookingFor(e.target.value)}
                          className="w-full px-2.5 py-2 rounded-xl border border-slate-300 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Preferred Companion Type</label>
                        <input
                          type="text"
                          value={clientCompanionType}
                          onChange={(e) => setClientCompanionType(e.target.value)}
                          className="w-full px-2.5 py-2 rounded-xl border border-slate-300 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Preferred Booking Duration</label>
                        <input
                          type="text"
                          value={clientDuration}
                          onChange={(e) => setClientDuration(e.target.value)}
                          className="w-full px-2.5 py-2 rounded-xl border border-slate-300 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Save & Continue
                  </button>
                </form>
              )}

              {/* STEP 4: CLIENT DASHBOARD (SPEC SECTION 2, STEP 4) */}
              {clientStep === 4 && (
                <div className="space-y-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-black text-slate-900">
                      Welcome to PairUp Today, {clientFullName}! 👋
                    </h3>
                    <div className="mt-2 inline-block px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full text-xs font-black uppercase tracking-wider">
                      FREE CLIENT ACCOUNT
                    </div>
                  </div>

                  {/* Quick Actions Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onFindCompanion?.();
                      }}
                      className="p-4 rounded-2xl border border-slate-200 hover:border-pink-300 hover:bg-pink-50/20 transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
                          <Search className="w-5 h-5" />
                        </div>
                        <div>
                          <strong className="text-xs font-bold text-slate-900 block">Find a Companion</strong>
                          <span className="text-[11px] text-slate-500">Explore verified hosts</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-pink-600 group-hover:translate-x-1 transition-all" />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenBookings?.();
                      }}
                      className="p-4 rounded-2xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/20 transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <strong className="text-xs font-bold text-slate-900 block">View Bookings</strong>
                          <span className="text-[11px] text-slate-500">Upcoming meets & OTPs</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenProfile?.();
                      }}
                      className="p-4 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/20 transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <strong className="text-xs font-bold text-slate-900 block">My Profile</strong>
                          <span className="text-[11px] text-slate-500">Manage account info</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenWallet?.();
                      }}
                      className="p-4 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/20 transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                          <Wallet className="w-5 h-5" />
                        </div>
                        <div>
                          <strong className="text-xs font-bold text-slate-900 block">Wallet & Payments</strong>
                          <span className="text-[11px] text-slate-500">Transactions & receipts</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
                  >
                    Start Browsing Companions Now
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* VIEW 3: COMPANION SIGNUP FLOW (SPEC SECTIONS 3 - 9)                       */}
          {/* ========================================================================= */}
          {currentMode === 'companion-signup' && (
            <div className="space-y-6">
              {companionStep === 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentMode('signup-choice')}
                  className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 cursor-pointer"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Account Types
                </button>
              )}

              {/* Step indicator */}
              <div className="flex items-center justify-between text-xs font-bold text-slate-400">
                <span>Companion Onboarding</span>
                <span>Step {companionStep} of 7</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-purple-600 h-full transition-all duration-300"
                  style={{ width: `${(companionStep / 7) * 100}%` }}
                />
              </div>

              {/* STEP 1: CREATE COMPANION ACCOUNT */}
              {companionStep === 1 && (
                <form onSubmit={handleCompSubmitStep1} className="space-y-4">
                  <div className="text-center max-w-sm mx-auto">
                    <h3 className="text-2xl font-black text-slate-900">Become a PairUp Today Companion</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Create your account, choose a membership plan, and start accepting bookings.
                    </p>
                  </div>

                  {/* Top Error Alert if any validation failed */}
                  {compError && (
                    <div
                      id="comp-signup-error-banner"
                      className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700 flex items-start gap-2 shadow-xs"
                    >
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p>{compError}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3.5 text-xs">
                    {/* Legal vs Display Name Notice */}
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-slate-50 border border-purple-100 rounded-2xl text-[11px] space-y-1">
                      <div className="flex items-center gap-1.5 font-bold text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0" />
                        <span>Identity Privacy & Verified Badge</span>
                      </div>
                      <p className="text-slate-600 leading-relaxed">
                        Clients will only see your <strong>Profile Name / Display Name</strong> with a{' '}
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-bold text-sky-700 bg-sky-100 border border-sky-200">
                          Verified ✓
                        </span>{' '}
                        badge. Your <strong>Full Legal Name</strong> is kept 100% private and used solely for Government ID verification & bank payouts.
                      </p>
                    </div>

                    {/* Full Legal Name (ID অনুযায়ী) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-800 font-bold">
                          Full Legal Name <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                          <Lock className="w-3 h-3 text-slate-400" /> ID অনুযায়ী (Private)
                        </span>
                      </div>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Priya Banerjee"
                        value={compLegalName}
                        onChange={(e) => {
                          setCompLegalName(e.target.value);
                          if (!compDisplayName) {
                            const first = e.target.value.trim().split(' ')[0];
                            if (first) setCompDisplayName(first);
                          }
                          if (compErrorField === 'legalName') {
                            setCompError(null);
                            setCompErrorField(null);
                          }
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                          compErrorField === 'legalName'
                            ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                            : 'border-slate-300 focus:border-purple-500'
                        }`}
                      />
                      <p className="text-[10px] text-slate-400 mt-1">
                        🔒 ID অনুযায়ী পুরো নাম। Only used for verification, payment processing, & compliance.
                      </p>
                    </div>

                    {/* Profile Name / Display Name (নিজের পছন্দমতো) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-800 font-bold">
                          Profile Name / Display Name <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                          👉 Public Profile Name
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          placeholder="e.g. Priya, Alex, Rahul, Travel Buddy Kolkata"
                          value={compDisplayName}
                          onChange={(e) => {
                            setCompDisplayName(e.target.value);
                            if (compErrorField === 'displayName') {
                              setCompError(null);
                              setCompErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 pr-24 rounded-xl border focus:outline-none transition-colors ${
                            compErrorField === 'displayName'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-purple-500'
                          }`}
                        />
                        {compDisplayName && (
                          <div className="absolute right-2.5 top-2.5 flex items-center gap-1 bg-sky-50 text-sky-700 px-2 py-0.5 rounded-md border border-sky-200 text-[10px] font-bold">
                            <CheckCircle2 className="w-3 h-3 text-sky-600" />
                            <span>Verified ✓</span>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        ✨ নিজের পছন্দমতো। Multiple companions can have the same display name.
                      </p>
                    </div>

                    {/* Email Address (Unique) */}
                    <div>
                      <label className="block text-slate-800 font-bold mb-1">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="priya@example.com"
                        value={compEmail}
                        onChange={(e) => {
                          setCompEmail(e.target.value);
                          if (compErrorField === 'email') {
                            setCompError(null);
                            setCompErrorField(null);
                          }
                        }}
                        onBlur={async () => {
                          if (compEmail.trim() && compEmail.includes('@')) {
                            const exists = await checkEmailRegistered(compEmail);
                            if (exists) {
                              setCompError('Email already exists. Please log in or use a different email.');
                              setCompErrorField('email');
                            }
                          }
                        }}
                        className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                          compErrorField === 'email'
                            ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                            : 'border-slate-300 focus:border-purple-500'
                        }`}
                      />
                      {compErrorField === 'email' ? (
                        <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          Email already exists. Please log in or use a different email.
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400 mt-1">Must be unique to your companion account.</p>
                      )}
                    </div>

                    {/* Phone Number (Unique) */}
                    <div>
                      <label className="block text-slate-800 font-bold mb-1">
                        Phone Number <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-2.5 text-slate-400 font-medium">+91</span>
                        <input
                          type="tel"
                          required
                          maxLength={10}
                          placeholder="98300 12345"
                          value={compMobile}
                          onChange={(e) => {
                            setCompMobile(e.target.value.replace(/\D/g, ''));
                            if (compErrorField === 'phone') {
                              setCompError(null);
                              setCompErrorField(null);
                            }
                          }}
                          onBlur={async () => {
                            if (compMobile.trim().length === 10) {
                              const exists = await checkPhoneRegistered(compMobile);
                              if (exists) {
                                setCompError('Phone number already exists. Please log in or use a different number.');
                                setCompErrorField('phone');
                              }
                            }
                          }}
                          className={`w-full pl-12 pr-3.5 py-2.5 rounded-xl border focus:outline-none font-mono transition-colors ${
                            compErrorField === 'phone'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-purple-500'
                          }`}
                        />
                      </div>
                      {compErrorField === 'phone' ? (
                        <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          Phone number already exists. Please log in or use a different number.
                        </p>
                      ) : (
                        <p className="text-[10px] text-slate-400 mt-1">Used for OTP verification & booking alerts.</p>
                      )}
                    </div>

                    {/* Date of Birth (18+ Enforced) */}
                    <DateOfBirthInput
                      id="auth-modal-comp-step1-dob"
                      value={compDob}
                      onChange={(nextDob, age) => {
                        setCompDob(nextDob);
                        if (age !== null) setCompAge(age);
                        if (compError) setCompError(null);
                      }}
                      variant="light"
                    />

                    {/* Password & Confirm Password */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">
                          Password <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="password"
                          required
                          placeholder="Min. 8 characters"
                          value={compPassword}
                          onChange={(e) => {
                            setCompPassword(e.target.value);
                            if (compErrorField === 'password') {
                              setCompError(null);
                              setCompErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                            compErrorField === 'password'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-purple-500'
                          }`}
                        />
                        {compErrorField === 'password' ? (
                          <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            Password does not meet requirements
                          </p>
                        ) : (
                          <p className="text-[10px] text-slate-400 mt-1">
                            8+ characters with letters & numbers/symbols
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">
                          Confirm Password <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="password"
                          required
                          placeholder="Re-enter password"
                          value={compConfirmPassword}
                          onChange={(e) => {
                            setCompConfirmPassword(e.target.value);
                            if (compErrorField === 'confirmPassword') {
                              setCompError(null);
                              setCompErrorField(null);
                            }
                          }}
                          className={`w-full px-3.5 py-2.5 rounded-xl border focus:outline-none transition-colors ${
                            compErrorField === 'confirmPassword'
                              ? 'border-rose-500 bg-rose-50/20 text-rose-900 focus:border-rose-500 ring-2 ring-rose-500/20'
                              : 'border-slate-300 focus:border-purple-500'
                          }`}
                        />
                        {compErrorField === 'confirmPassword' ? (
                          <p className="text-[11px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            Passwords do not match
                          </p>
                        ) : compConfirmPassword && compPassword === compConfirmPassword ? (
                          <p className="text-[11px] text-emerald-600 font-bold mt-1 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5 shrink-0" />
                            Passwords match
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="pt-1">
                      <label className="flex items-start gap-2 cursor-pointer text-slate-600 text-xs">
                        <input
                          type="checkbox"
                          checked={compAgreedTerms}
                          onChange={(e) => setCompAgreedTerms(e.target.checked)}
                          className="w-4 h-4 rounded accent-purple-600 mt-0.5 cursor-pointer"
                        />
                        <span>I agree to the Terms of Service, Privacy Policy and Companion Guidelines.</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={compIsValidating}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-75"
                  >
                    {compIsValidating ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Validating Account...</span>
                      </>
                    ) : (
                      <span>Create Companion Account</span>
                    )}
                  </button>
                </form>
              )}

              {/* STEP 2: COMPANION MOBILE VERIFICATION */}
              {companionStep === 2 && (
                <form onSubmit={handleCompVerifyOtp} className="space-y-5 text-center max-w-sm mx-auto">
                  <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mx-auto">
                    <Smartphone className="w-6 h-6" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-black text-slate-900">Verify Your Mobile Number</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Enter the 6-digit OTP sent to your mobile number <strong className="text-slate-800">+91 {compMobile}</strong>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-2">Enter OTP</label>
                    <div className="flex justify-center gap-2">
                      {compOtp.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={(el) => {
                            compOtpInputs.current[idx] = el;
                          }}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            const next = [...compOtp];
                            next[idx] = val;
                            setCompOtp(next);
                            if (val && idx < 5) {
                              compOtpInputs.current[idx + 1]?.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !digit && idx > 0) {
                              compOtpInputs.current[idx - 1]?.focus();
                            }
                          }}
                          className="w-11 h-12 text-center font-mono font-bold text-lg border border-slate-300 rounded-xl focus:border-purple-500 focus:outline-none bg-slate-50"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      type="submit"
                      className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                    >
                      Verify & Continue
                    </button>

                    <button
                      type="button"
                      disabled={compOtpTimer > 0}
                      onClick={handleCompResendOtp}
                      className="text-xs font-bold text-slate-600 hover:text-slate-900 disabled:opacity-50 cursor-pointer py-1"
                    >
                      {compOtpTimer > 0 ? `Resend OTP in ${compOtpTimer} seconds` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 3: COMPANION IDENTITY VERIFICATION (SPEC SECTION 5) */}
              {companionStep === 3 && (
                <form onSubmit={handleCompSubmitIdentity} className="space-y-4">
                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-2">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900">Verify Your Identity</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      To keep PairUp Today safe and trusted, companions must verify their identity before activating their profile.
                    </p>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Select Government ID</label>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                        {['Aadhaar', 'PAN', 'Passport', 'Driving Licence', 'Voter ID'].map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setCompIdType(type)}
                            className={`p-2 rounded-xl text-center font-bold border transition-all text-[11px] cursor-pointer ${
                              compIdType === type
                                ? 'bg-purple-50 border-purple-500 text-purple-700 ring-1 ring-purple-500'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Government ID Number</label>
                      <input
                        type="text"
                        required
                        placeholder={`Enter your ${compIdType} number`}
                        value={compIdNumber}
                        onChange={(e) => setCompIdNumber(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-mono uppercase"
                      />
                    </div>

                    {/* Upload ID Document & Take Selfie */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      {/* 1. Upload ID Document with Live Camera / File */}
                      <div
                        id="comp-doc-upload-box"
                        className={`p-3.5 border-2 border-dashed rounded-2xl text-center space-y-2 transition-all ${
                          compIdDocUploaded
                            ? 'border-emerald-400 bg-emerald-50/50'
                            : 'border-slate-300 bg-slate-50 hover:border-purple-300'
                        }`}
                      >
                        {compIdDocUploaded && compIdDocUrl && compIdDocUrl !== 'pdf_placeholder' ? (
                          <div className="relative w-16 h-12 mx-auto rounded-lg overflow-hidden border border-emerald-300 shadow-xs">
                            <img
                              src={compIdDocUrl}
                              alt="ID preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-emerald-950/20 flex items-center justify-center">
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-9 h-9 mx-auto rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                            <Upload className="w-5 h-5" />
                          </div>
                        )}
                        <div>
                          <span className="font-bold text-slate-800 text-xs block">
                            Upload ID Document
                          </span>
                          <p className="text-[10px] text-slate-500 truncate max-w-[180px] mx-auto">
                            {compIdDocName || 'ক্যামেরা বা ফাইল দিয়ে আপলোড করুন'}
                          </p>
                        </div>
                        <button
                          type="button"
                          id="btn-open-comp-doc-modal"
                          onClick={() => setIsCompDocModalOpen(true)}
                          className={`px-3 py-1.5 text-[11px] font-bold rounded-xl cursor-pointer transition-all shadow-xs ${
                            compIdDocUploaded
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          }`}
                        >
                          {compIdDocUploaded ? '✓ Document Uploaded (Change)' : 'Browse / Camera'}
                        </button>
                      </div>

                      {/* 2. Take a Selfie with Automatic Face Verification (No sunglasses, No cap) */}
                      <div
                        id="comp-selfie-verify-box"
                        className={`p-3.5 border-2 border-dashed rounded-2xl text-center space-y-2 transition-all ${
                          compSelfieUploaded
                            ? 'border-emerald-400 bg-emerald-50/50'
                            : 'border-slate-300 bg-slate-50 hover:border-purple-300'
                        }`}
                      >
                        {compSelfieUploaded && compSelfieUrl ? (
                          <div className="relative w-14 h-14 mx-auto rounded-full overflow-hidden border-2 border-emerald-400 shadow-sm">
                            <img
                              src={compSelfieUrl}
                              alt="Selfie preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-emerald-950/10 flex items-center justify-center">
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-9 h-9 mx-auto rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                            <Camera className="w-5 h-5" />
                          </div>
                        )}
                        <div>
                          <span className="font-bold text-slate-800 text-xs block">
                            Take a Selfie
                          </span>
                          <p className="text-[10px] text-slate-500">
                            {compSelfieUploaded
                              ? '✓ 100% Face Verified'
                              : 'সানগ্লাস ও টুপি ছাড়া স্বয়ংক্রিয় ভেরিফিকেশন'}
                          </p>
                        </div>
                        <button
                          type="button"
                          id="btn-open-comp-selfie-modal"
                          onClick={() => setIsCompSelfieModalOpen(true)}
                          className={`px-3 py-1.5 text-[11px] font-bold rounded-xl cursor-pointer transition-all shadow-xs ${
                            compSelfieUploaded
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          }`}
                        >
                          {compSelfieUploaded ? '✓ Verified (Retake)' : 'Take / Verify Selfie'}
                        </button>
                      </div>
                    </div>

                    <div className="pt-2">
                      <label className="flex items-start gap-2 cursor-pointer text-slate-600 text-xs">
                        <input
                          type="checkbox"
                          checked={compAgreedIdentityAccuracy}
                          onChange={(e) => setCompAgreedIdentityAccuracy(e.target.checked)}
                          className="w-4 h-4 rounded accent-purple-600 mt-0.5 cursor-pointer"
                        />
                        <span>I confirm that the information provided is accurate and belongs to me.</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Submit for Verification
                  </button>

                  <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold">Verification in Progress</strong>
                      <span className="text-[11px] text-amber-800">
                        Your identity verification is being reviewed. You can continue setting up your profile while verification is in progress.
                      </span>
                    </div>
                  </div>
                </form>
              )}

              {/* STEP 4: COMPANION PROFILE SETUP (SPEC SECTION 6) */}
              {companionStep === 4 && (
                <form onSubmit={handleCompSaveProfile} className="space-y-4">
                  <div className="text-center max-w-sm mx-auto">
                    <h3 className="text-2xl font-black text-slate-900">Create Your Companion Profile</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Customize your public profile, set your hourly rate & availability, and preview how clients see you.
                    </p>
                  </div>

                  <div className="space-y-3.5 text-xs">
                    {/* Legal vs Public Name Verification Banner */}
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-1.5 font-bold text-slate-800">
                          <Lock className="w-3.5 h-3.5 text-slate-500" />
                          <span>Full Legal Name (ID অনুযায়ী)</span>
                        </div>
                        <p className="text-slate-600 font-semibold text-xs mt-0.5">
                          {compLegalName || compFullName || 'Identity Verified'}
                        </p>
                        <span className="text-[10px] text-slate-400">
                          🔒 Kept 100% private for KYC, payouts & compliance.
                        </span>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[10px] border border-emerald-200 flex items-center gap-1 shrink-0">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" /> ID Verified ✓
                      </span>
                    </div>

                    {/* Profile Name / Display Name (Public) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-800 font-bold">
                          Profile Name / Display Name (নিজের পছন্দমতো) <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
                          👉 Shown on Public Profile
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          placeholder="e.g. Rahul, Riya, Alex, Travel Buddy Kolkata"
                          value={compDisplayName}
                          onChange={(e) => setCompDisplayName(e.target.value)}
                          className="w-full px-3.5 py-2.5 pr-24 rounded-xl border border-slate-300 focus:border-purple-500 focus:outline-none"
                        />
                        {compDisplayName && (
                          <div className="absolute right-2.5 top-2.5 flex items-center gap-1 bg-sky-50 text-sky-700 px-2 py-0.5 rounded-md border border-sky-200 text-[10px] font-bold">
                            <CheckCircle2 className="w-3 h-3 text-sky-600" />
                            <span>Verified ✓</span>
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Public profile-এ দেখাবে (যেমন: Rahul, Riya, Alex, Travel Buddy Kolkata ইত্যাদি)।
                      </p>
                    </div>

                    {/* Profile Photo */}
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200">
                      <img
                        src={compPhoto}
                        alt="Preview"
                        className="w-14 h-14 rounded-2xl object-cover border-2 border-purple-400"
                      />
                      <div className="flex-1">
                        <span className="font-bold text-slate-800 block">Profile Photo</span>
                        <span className="text-[10px] text-slate-500">Upload your friendly profile picture</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {[
                            'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
                            'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
                            'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=400&q=80',
                            'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
                          ].map((u, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setCompPhoto(u)}
                              className="text-[10px] px-2 py-0.5 rounded bg-white border border-slate-300 font-medium hover:border-purple-500 cursor-pointer"
                            >
                              Sample {i + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Date of Birth, Age, Gender & Location */}
                    <DateOfBirthInput
                      id="auth-modal-comp-step4-dob"
                      value={compDob}
                      onChange={(nextDob, age) => {
                        setCompDob(nextDob);
                        if (age !== null) setCompAge(age);
                      }}
                      variant="light"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Age (18+)</label>
                        <input
                          type="number"
                          min={18}
                          max={100}
                          value={compAge}
                          onChange={(e) => setCompAge(Math.max(18, Number(e.target.value) || 18))}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Gender</label>
                        <select
                          value={compGender}
                          onChange={(e) => setCompGender(e.target.value as any)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white focus:outline-none"
                        >
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Location / City</label>
                        <input
                          type="text"
                          value={compCity}
                          onChange={(e) => setCompCity(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Languages & Availability */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Languages Spoken</label>
                        <input
                          type="text"
                          value={compLanguages}
                          onChange={(e) => setCompLanguages(e.target.value)}
                          placeholder="e.g. English, Bengali, Hindi"
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Availability</label>
                        <select
                          value={compAvailability}
                          onChange={(e) => setCompAvailability(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white font-medium focus:outline-none"
                        >
                          <option value="Flexible Hours (Evenings & Weekends)">Flexible Hours (Evenings & Weekends)</option>
                          <option value="Weekdays (9 AM - 6 PM)">Weekdays (9 AM - 6 PM)</option>
                          <option value="Weekends Only (Saturday & Sunday)">Weekends Only (Saturday & Sunday)</option>
                          <option value="Full-Time (Anytime by Appointment)">Full-Time (Anytime by Appointment)</option>
                          <option value="Night Events & Dinners (7 PM - 12 AM)">Night Events & Dinners (7 PM - 12 AM)</option>
                        </select>
                      </div>
                    </div>

                    {/* Bio */}
                    <div>
                      <label className="block text-slate-700 font-bold mb-1">Bio / About Me</label>
                      <textarea
                        rows={2}
                        value={compAboutMe}
                        onChange={(e) => setCompAboutMe(e.target.value)}
                        placeholder="Tell clients about your personality, interests, and what kind of conversations you enjoy..."
                        className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:outline-none"
                      />
                    </div>

                    {/* Skills & Experience */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Skills & Specialties</label>
                        <input
                          type="text"
                          value={compSkills}
                          onChange={(e) => setCompSkills(e.target.value)}
                          placeholder="e.g. Dining etiquette, Photography, City tours"
                          className="w-full px-2.5 py-2 rounded-xl border border-slate-300 text-xs focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Experience / Background</label>
                        <input
                          type="text"
                          value={compExperience}
                          onChange={(e) => setCompExperience(e.target.value)}
                          placeholder="e.g. 2+ years hosting social & cultural meetups"
                          className="w-full px-2.5 py-2 rounded-xl border border-slate-300 text-xs focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Services Checklist */}
                    <div>
                      <label className="block text-slate-700 font-bold mb-1.5">
                        Services Offered
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          'Social companionship',
                          'Conversation',
                          'Events',
                          'Travel companionship',
                          'Study companionship',
                          'Activity companionship',
                          'Senior companionship',
                          'Other',
                        ].map((srv) => (
                          <label
                            key={srv}
                            className={`p-2 rounded-xl border flex items-center gap-1.5 cursor-pointer text-[11px] ${
                              compServices.includes(srv)
                                ? 'bg-purple-50 border-purple-500 text-purple-900 font-bold'
                                : 'bg-white border-slate-200 text-slate-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={compServices.includes(srv)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCompServices([...compServices, srv]);
                                } else {
                                  setCompServices(compServices.filter((s) => s !== srv));
                                }
                              }}
                              className="w-3.5 h-3.5 accent-purple-600 rounded"
                            />
                            <span>{srv}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Hourly Rate */}
                    <div className="p-4 bg-purple-50/60 border border-purple-200 rounded-2xl grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">Hourly Rate (₹ / hour)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 font-bold text-slate-600">₹</span>
                          <input
                            type="number"
                            min={300}
                            step={100}
                            value={compHourlyRate}
                            onChange={(e) => setCompHourlyRate(Number(e.target.value))}
                            className="w-full pl-8 pr-3 py-2 bg-white border border-purple-300 rounded-xl font-bold font-mono text-base text-purple-900"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-slate-800 font-bold mb-1">Minimum Booking Duration</label>
                        <input
                          type="text"
                          readOnly
                          value="1 hour"
                          className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-xl font-semibold text-slate-700"
                        />
                      </div>
                    </div>

                    {/* Live Public Profile Preview Card */}
                    <div className="p-3.5 bg-gradient-to-br from-slate-900 to-purple-950 text-white rounded-2xl space-y-2.5 shadow-sm">
                      <div className="flex items-center justify-between text-[11px] text-purple-300 font-bold">
                        <span>Public Profile Preview (What clients see)</span>
                        <span className="text-emerald-400 flex items-center gap-1 font-bold">
                          <Eye className="w-3 h-3" /> Live
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <img
                          src={compPhoto}
                          alt="Preview"
                          className="w-12 h-12 rounded-xl object-cover border border-purple-400"
                        />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-sm text-white">
                              {compDisplayName || 'Your Display Name'}
                            </span>
                            <span className="px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-400/30 text-[10px] font-black">
                              Verified ✓
                            </span>
                          </div>
                          <p className="text-[11px] text-purple-200 mt-0.5">
                            {compCity} • ₹{compHourlyRate}/hr • {compAvailability}
                          </p>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-300 italic border-t border-purple-800/60 pt-1.5">
                        🔒 Full legal name is hidden. Only display name & verified status are visible.
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Save & Continue to Membership
                  </button>
                </form>
              )}

              {/* STEP 5: MEMBERSHIP SELECTION (SPEC SECTION 7) */}
              {companionStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center max-w-md mx-auto">
                    <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-2">
                      <Crown className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900">Choose Your Companion Membership</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      A membership plan is required to activate your Companion profile and start accepting bookings.
                    </p>
                  </div>

                  {/* 3 Package Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                    {[
                      {
                        id: 'STARTER' as CompanionMembershipPlan,
                        name: 'STARTER',
                        price: 449,
                        period: '/ month',
                        popular: false,
                        buttonText: 'Choose Starter',
                        features: [
                          'Create Companion Profile',
                          'Receive Client Requests',
                          'Accept/Reject Bookings',
                          'Basic Profile Visibility',
                          'Booking Dashboard',
                          'Earnings Dashboard',
                        ],
                      },
                      {
                        id: 'PROFESSIONAL' as CompanionMembershipPlan,
                        name: 'PROFESSIONAL',
                        price: 1099,
                        period: '/ month',
                        popular: true,
                        buttonText: 'Choose Professional',
                        features: [
                          'Everything in Starter',
                          'Higher Profile Visibility',
                          'AI-powered Client Matching',
                          'Priority Booking Opportunities',
                          'Advanced Earnings Dashboard',
                          'Performance Insights',
                          'Faster Support',
                        ],
                      },
                      {
                        id: 'PREMIUM' as CompanionMembershipPlan,
                        name: 'PREMIUM',
                        price: 5999,
                        period: '/ month',
                        popular: false,
                        buttonText: 'Choose Premium',
                        features: [
                          'Everything in Professional',
                          'Premium Profile Visibility',
                          'Priority Client Matching',
                          'Featured Profile Placement',
                          'Advanced Analytics',
                          'Priority Support',
                          'Premium Companion Badge',
                        ],
                      },
                    ].map((plan) => (
                      <div
                        key={plan.name}
                        className={`rounded-2xl p-4 flex flex-col justify-between border transition-all ${
                          plan.popular
                            ? 'border-purple-500 bg-gradient-to-b from-purple-50/60 to-white shadow-lg relative'
                            : 'border-slate-200 bg-white shadow-2xs'
                        }`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-[9px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full shadow-xs">
                            ★ MOST POPULAR
                          </div>
                        )}

                        <div>
                          <h4 className="font-extrabold text-slate-900 text-base mb-1">{plan.name}</h4>
                          <div className="flex items-baseline gap-1 mb-4">
                            <span className="text-2xl font-black text-slate-900">₹{plan.price}</span>
                            <span className="text-[10px] text-slate-500">{plan.period}</span>
                          </div>

                          <div className="space-y-1.5 mb-5 text-[11px] text-slate-600">
                            {plan.features.map((feat, i) => (
                              <div key={i} className="flex items-start gap-1.5">
                                <Check className="w-3.5 h-3.5 text-purple-600 shrink-0 mt-0.5" />
                                <span>{feat}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleCompChoosePlan(plan)}
                          className={`w-full py-2.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                            plan.popular
                              ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md'
                              : 'bg-slate-900 hover:bg-slate-800 text-white'
                          }`}
                        >
                          {plan.buttonText}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t border-slate-100 text-center space-y-1 text-[11px] text-slate-400">
                    <p>Membership automatically renews according to your selected billing cycle unless cancelled.</p>
                    <p>You can change or cancel your membership from your Companion Dashboard.</p>
                  </div>
                </div>
              )}

              {/* STEP 6: MEMBERSHIP CHECKOUT (SPEC SECTION 8 & BACKEND ACTIVATION RULE) */}
              {companionStep === 6 && (
                <div className="space-y-5 max-w-md mx-auto">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setCompanionStep(5)}
                      className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 cursor-pointer"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Change Plan
                    </button>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                      Profile Inactive (Payment Required)
                    </span>
                  </div>

                  <div className="text-center">
                    <h3 className="text-2xl font-black text-slate-900">Payment & Profile Activation</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Backend gateway verification is required before your Companion profile can be activated.
                    </p>
                  </div>

                  {/* Order & Rule Summary Card */}
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-50 to-slate-50 border border-purple-200 text-xs space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-purple-700 uppercase">Selected Plan</span>
                        <h4 className="text-base font-black text-slate-900">{selectedPlan.name}</h4>
                        <span className="text-slate-500 text-[11px]">
                          Billing: <strong>Monthly</strong> • Next: <strong>{nextBillingDate}</strong>
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-black text-slate-900">₹{selectedPlan.price}</span>
                        <span className="text-slate-500 block text-[10px]">/ month</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-purple-200/60 flex items-center justify-between text-[10px] text-slate-600">
                      <span>Order ID: <strong className="font-mono text-slate-800">{currentOrderId || 'Generating...'}</strong></span>
                      <span className="text-purple-700 font-bold flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3 text-purple-600" /> Gateway Protected
                      </span>
                    </div>
                  </div>

                  {/* Backend Verification Notice */}
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-[11px] text-amber-900 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="font-bold">Authoritative Backend Rule:</p>
                      <p className="text-amber-800 text-[10px] leading-relaxed">
                        Profile is activated <strong>ONLY</strong> when payment gateway status is <em>Success</em> and cryptographically verified by the server. If unpaid or cancelled, the profile remains <strong>Inactive</strong> and is hidden from search and client bookings.
                      </p>
                    </div>
                  </div>

                  {/* Error / Inactive State Banner */}
                  {(paymentFailedState || paymentErrorMessage) && (
                    <div className="p-4 rounded-2xl bg-rose-50 border-2 border-rose-200 text-xs text-rose-900 space-y-2 animate-in fade-in">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-extrabold text-rose-900">Payment Failed / Cancelled — Profile Inactive</h4>
                          <p className="text-[11px] text-rose-700 mt-0.5">{paymentErrorMessage}</p>
                        </div>
                      </div>
                      <div className="p-2.5 bg-white/70 rounded-xl text-[10px] text-slate-700 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>Profile Status:</span>
                          <strong className="text-rose-600">🔴 Inactive</strong>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Public Search & Listings:</span>
                          <strong className="text-slate-600">Hidden</strong>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Bookings & Earnings:</span>
                          <strong className="text-slate-600">Disabled until payment</strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Real Razorpay Checkout — no manual QR or fake card fields */}
                  {currentOrderId && currentOrderKeyId && currentOrderAmountPaise > 0 ? (
                    <RazorpayCheckout
                      orderId={currentOrderId}
                      keyId={currentOrderKeyId}
                      amountPaise={currentOrderAmountPaise}
                      currency="INR"
                      description={`${selectedPlan.name} membership`}
                      customerName={compDisplayName.trim() || compFullName.trim() || 'Companion Host'}
                      customerEmail={compEmail}
                      customerPhone={compMobile}
                      onPaymentResponse={handleCompCompletePayment}
                      onDismiss={() => {
                        setPaymentVerificationStatus('FAILED');
                        setPaymentFailedState(true);
                        setPaymentErrorMessage(
                          'Checkout was closed. No membership was activated. You may retry payment.'
                        );
                      }}
                      onError={(message) => {
                        setPaymentVerificationStatus('FAILED');
                        setPaymentFailedState(true);
                        setPaymentErrorMessage(message);
                      }}
                    />
                  ) : (
                    <div className="p-4 rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-800">
                      Secure payment order is unavailable. No payment can be accepted and membership remains inactive.
                    </div>
                  )}

                </div>
              )}

              {/* STEP 7: PAYMENT SUCCESS PAGE (SPEC SECTION 9 & USER REQUIREMENT 2) */}
              {companionStep === 7 && (
                <div className="space-y-6 text-center max-w-md mx-auto py-2 animate-in zoom-in-95">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-900">
                      ✓ Payment Successful
                    </h3>
                    <h4 className="text-base font-bold text-emerald-600">
                      Membership Activated
                    </h4>
                    <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5 pt-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      Redirecting automatically to your dashboard...
                    </p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 text-left space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Membership:</span>
                      <span className="font-extrabold text-slate-900">{selectedPlan.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Order ID:</span>
                      <span className="font-mono text-slate-800">{currentOrderId}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Status:</span>
                      <span className="font-bold text-emerald-600 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        🟢 ACTIVE
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Next billing date:</span>
                      <span className="font-bold text-slate-800">{nextBillingDate}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenProfile?.();
                      }}
                      className="w-full py-3 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                    >
                      Complete My Profile
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenCompanionDashboard?.();
                      }}
                      className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                    >
                      Go to Companion Dashboard Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* VIEW 4: LOGIN PAGE (SPEC SECTION 16)                                      */}
          {/* ========================================================================= */}
          {currentMode === 'login' && (
            <div className="space-y-5 max-w-sm mx-auto">
              {isForgotPasswordMode ? (
                <div className="space-y-4 text-xs">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-2xl bg-pink-100 text-pink-600 mx-auto flex items-center justify-center mb-2 shadow-xs">
                      <KeyRound className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-black text-slate-900">Reset Password</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {forgotStep === 1
                        ? 'Enter your registered email or mobile to receive a reset code'
                        : `Create a new password for ${forgotIdentifier}`}
                    </p>
                  </div>

                  {forgotSuccess && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                      <span>{forgotSuccess}</span>
                    </div>
                  )}

                  {forgotAccountNotFound ? (
                    <div className="p-4 bg-rose-50 border-2 border-rose-200 rounded-2xl text-xs text-rose-900 space-y-3">
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0 text-rose-600">
                          <UserX className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-extrabold text-slate-900 text-sm">Account Not Found</h4>
                          <p className="font-semibold text-rose-700 mt-0.5">
                            “No account found with this email/phone. Please sign up first.”
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setCurrentMode('signup-choice');
                        }}
                        className="w-full py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold rounded-xl text-xs shadow-md cursor-pointer"
                      >
                        Sign Up
                      </button>
                    </div>
                  ) : forgotError ? (
                    <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{forgotError}</span>
                    </div>
                  ) : null}

                  {forgotStep === 1 ? (
                    <form onSubmit={handleForgotSendOtp} className="space-y-3.5">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Registered 10-Digit Mobile Number</label>
                        <div className="relative flex items-center">
                          <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                            +91
                          </span>
                          <input
                            type="tel"
                            inputMode="numeric"
                            required
                            maxLength={10}
                            placeholder="9876543210"
                            value={forgotIdentifier}
                            onChange={(e) => {
                              setForgotIdentifier(e.target.value.replace(/\D/g, '').slice(0, 10));
                              setForgotError(null);
                              setForgotAccountNotFound(false);
                            }}
                            className="w-full pl-12 pr-3.5 py-2.5 rounded-xl border border-slate-300 font-mono font-bold text-slate-900 focus:border-pink-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={forgotIsValidating}
                        className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {forgotIsValidating ? (
                          <>
                            <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Checking Account...</span>
                          </>
                        ) : (
                          <span>Send Reset Code</span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setForgotError(null);
                          setForgotAccountNotFound(false);
                        }}
                        className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors cursor-pointer text-center"
                      >
                        Back to Login
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleForgotResetPasswordSubmit} className="space-y-3.5">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1 text-center">6-Digit Verification Code</label>
                        <div className="flex justify-center gap-2 mb-2">
                          {forgotOtpCode.map((d, i) => (
                            <input
                              key={i}
                              type="text"
                              maxLength={1}
                              value={d}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                const next = [...forgotOtpCode];
                                next[i] = val;
                                setForgotOtpCode(next);
                              }}
                              className="w-10 h-11 text-center font-mono font-bold border border-slate-300 rounded-xl focus:border-pink-500 bg-slate-50"
                            />
                          ))}
                        </div>
                        <div className="text-center">
                          <button
                            type="button"
                            disabled={forgotOtpTimer > 0}
                            onClick={handleForgotResendOtp}
                            className="text-pink-600 hover:text-pink-700 font-bold disabled:text-slate-400 cursor-pointer disabled:cursor-not-allowed text-[11px]"
                          >
                            {forgotOtpTimer > 0 ? `Resend code in ${forgotOtpTimer}s` : 'Resend Code'}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-slate-700 font-bold mb-1">New Password</label>
                        <input
                          type="password"
                          required
                          placeholder="Min 8 chars (Uppercase, lowercase, number)"
                          value={forgotNewPassword}
                          onChange={(e) => setForgotNewPassword(e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-pink-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-slate-700 font-bold mb-1">Confirm New Password</label>
                        <input
                          type="password"
                          required
                          placeholder="Repeat new password"
                          value={forgotConfirmPassword}
                          onChange={(e) => setForgotConfirmPassword(e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-pink-500 focus:outline-none"
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer"
                      >
                        Update Password & Log In
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setForgotError(null);
                          setForgotAccountNotFound(false);
                        }}
                        className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors cursor-pointer text-center"
                      >
                        Cancel & Back to Login
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <h3 className="text-2xl font-black text-slate-900">Welcome Back</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Log in to your PairUp Today account
                    </p>
                  </div>

                  {loginPasswordIncorrect ? (
                    <div
                      id="wrong-password-alert-box"
                      className="p-4 bg-rose-50 border-2 border-rose-300 rounded-2xl text-xs space-y-3 animate-in fade-in"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0 text-rose-600">
                          <Lock className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-extrabold text-slate-900 text-sm">Login Blocked</h4>
                          <p className="font-bold text-rose-700 mt-0.5 text-xs">
                            Incorrect password. Please try again.
                          </p>
                        </div>
                      </div>

                      <div className="pt-1 space-y-2">
                        <button
                          type="button"
                          id="btn-login-try-again"
                          onClick={() => {
                            setLoginPasswordIncorrect(false);
                            setLoginPassword('');
                            setLoginError(null);
                            setTimeout(() => {
                              passwordInputRef.current?.focus();
                            }, 50);
                          }}
                          className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          <span>Try Again</span>
                        </button>

                        <div className="text-center pt-1">
                          <button
                            type="button"
                            id="btn-login-forgot-password"
                            onClick={() => {
                              setForgotIdentifier(loginIdentifier);
                              setIsForgotPasswordMode(true);
                              setForgotStep(1);
                              setForgotError(null);
                              setLoginPasswordIncorrect(false);
                            }}
                            className="text-xs text-slate-600 hover:text-pink-600 font-semibold cursor-pointer underline underline-offset-2 transition-colors inline-flex items-center gap-1"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                            <span>Forgot Password?</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : loginAccountNotFound ? (
                    <div
                      id="account-not-found-banner"
                      className="p-4 bg-rose-50 border-2 border-rose-200 rounded-2xl text-xs text-rose-900 space-y-3 animate-in fade-in"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0 text-rose-600">
                          <UserX className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-extrabold text-slate-900 text-sm">Account Not Found</h4>
                          <p className="font-semibold text-rose-700 mt-0.5">
                            “No account found with this email/phone. Please sign up first.”
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        id="login-account-not-found-signup-btn"
                        onClick={() => {
                          const digits = loginIdentifier.replace(/\D/g, '').slice(-10);
                          setClientMobile(digits);
                          setCompMobile(digits);
                          setLoginAccountNotFound(false);
                          setLoginError(null);
                          setCurrentMode('signup-choice');
                        }}
                        className="w-full py-2.5 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>Sign Up</span>
                      </button>
                    </div>
                  ) : loginError ? (
                    <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{loginError}</span>
                    </div>
                  ) : null}

                  {!loginIsOtpMode ? (
                    <form onSubmit={handleStandardLogin} className="space-y-3.5 text-xs">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1">10-Digit Mobile Number</label>
                        <div className="relative flex items-center">
                          <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                            +91
                          </span>
                          <input
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            required
                            maxLength={10}
                            placeholder="9876543210"
                            value={loginIdentifier}
                            onChange={(e) => {
                              setLoginIdentifier(e.target.value.replace(/\D/g, '').slice(0, 10));
                              setLoginError(null);
                              setLoginAccountNotFound(false);
                              setLoginPasswordIncorrect(false);
                            }}
                            className="w-full pl-12 pr-10 py-2.5 rounded-xl border border-slate-300 font-mono font-bold text-slate-900 focus:border-pink-500 focus:outline-none"
                          />
                          <Smartphone className="w-4 h-4 text-slate-400 absolute right-3 pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-slate-700 font-bold">Password</label>
                          <button
                            type="button"
                            onClick={() => {
                              setForgotIdentifier(loginIdentifier);
                              setIsForgotPasswordMode(true);
                              setForgotStep(1);
                              setForgotError(null);
                              setLoginPasswordIncorrect(false);
                            }}
                            className="text-[11px] text-pink-600 hover:text-pink-700 font-medium cursor-pointer"
                          >
                            Forgot Password?
                          </button>
                        </div>
                        <input
                          ref={passwordInputRef}
                          type="password"
                          required
                          placeholder="••••••••"
                          value={loginPassword}
                          onChange={(e) => {
                            setLoginPassword(e.target.value);
                            setLoginError(null);
                            setLoginPasswordIncorrect(false);
                          }}
                          className={`w-full px-3.5 py-2.5 rounded-xl border ${
                            loginPasswordIncorrect
                              ? 'border-rose-400 bg-rose-50/40 text-rose-900 focus:border-rose-500'
                              : 'border-slate-300 focus:border-pink-500'
                          } focus:outline-none`}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loginIsValidating}
                        className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {loginIsValidating ? (
                          <>
                            <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Checking Account...</span>
                          </>
                        ) : (
                          <span>Log In</span>
                        )}
                      </button>

                      <button
                        type="button"
                        disabled={loginIsValidating}
                        onClick={handleLoginWithOtpRequest}
                    className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    <span>Continue with OTP</span>
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLoginOtpVerify} className="space-y-4 text-center">
                  <p className="text-xs text-slate-500">
                    Enter the 6-digit OTP code sent to <strong className="text-slate-800">{loginIdentifier}</strong>
                  </p>

                  <div className="flex justify-center gap-2">
                    {loginOtpCode.map((d, i) => (
                      <input
                        key={i}
                        type="text"
                        maxLength={1}
                        value={d}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          const next = [...loginOtpCode];
                          next[i] = val;
                          setLoginOtpCode(next);
                        }}
                        className="w-10 h-11 text-center font-mono font-bold border border-slate-300 rounded-xl focus:border-pink-500 bg-slate-50"
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer"
                  >
                    Verify & Log In
                  </button>

                  <div className="flex items-center justify-between text-xs pt-1">
                    <button
                      type="button"
                      disabled={loginOtpTimer > 0}
                      onClick={handleLoginResendOtp}
                      className="text-pink-600 hover:text-pink-700 font-bold disabled:text-slate-400 cursor-pointer disabled:cursor-not-allowed"
                    >
                      {loginOtpTimer > 0 ? `Resend code in ${loginOtpTimer}s` : 'Resend Code'}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setLoginIsOtpMode(false);
                        setLoginError(null);
                      }}
                      className="text-slate-500 hover:text-slate-800 font-bold cursor-pointer"
                    >
                      Use Password Instead
                    </button>
                  </div>
                </form>
              )}

              {/* Below: Don't have an account? Sign Up */}
              <div className="pt-3 text-center border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setCurrentMode('signup-choice')}
                    className="font-bold text-pink-600 hover:text-pink-700 underline cursor-pointer ml-1"
                  >
                    Sign Up
                  </button>
                </p>
              </div>
                </>
              )}
            </div>
          )}

        </div>

      </div>

      {/* Client Document Upload Modal (Camera & File) */}
      <DocumentUploadModal
        isOpen={isClientDocModalOpen}
        onClose={() => setIsClientDocModalOpen(false)}
        documentType={clientIdType}
        currentDocumentUrl={clientIdDocUrl}
        onDocumentUploaded={(doc) => {
          setClientIdDocUrl(doc.url);
          setClientIdDocUploaded(true);
        }}
      />

      {/* Companion Document Upload Modal (Camera & File) */}
      <DocumentUploadModal
        isOpen={isCompDocModalOpen}
        onClose={() => setIsCompDocModalOpen(false)}
        documentType={compIdType}
        currentDocumentUrl={compIdDocUrl}
        onDocumentUploaded={(doc) => {
          setCompIdDocUrl(doc.url);
          setCompIdDocName(doc.name);
          setCompIdDocUploaded(true);
        }}
      />

      {/* Companion Face Verification Modal (Live Camera, No Sunglasses, No Cap, Auto-verify) */}
      <FaceVerificationModal
        isOpen={isCompSelfieModalOpen}
        onClose={() => setIsCompSelfieModalOpen(false)}
        currentSelfieUrl={compSelfieUrl}
        userName={compDisplayName || 'Companion'}
        onSelfieVerified={(selfieUrl) => {
          setCompSelfieUrl(selfieUrl);
          setCompSelfieUploaded(true);
          // Auto-set as profile photo if user still has default avatar
          if (!compPhoto || compPhoto.includes('unsplash.com')) {
            setCompPhoto(selfieUrl);
          }
        }}
      />
    </div>
  );
};
